/**
 * Page builder (ADR-0010 P7c1) — author a page's AST visually, no JSON.
 *
 * Three panes: a component **palette** (click to add under the selected
 * container), an **outline + live preview** (select / reorder / delete +
 * the real PageRenderer fed by admin-fetched rows), and a schema-driven
 * **inspector** for the selected node. Plus page settings + a queries
 * editor. Save PATCHes the whole AST — the backend's save-time validator
 * is the backstop, so a bad binding 422s here.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  AlarmClock,
  AppWindow,
  ArrowLeft,
  BookOpen,
  BookText,
  Layers,
  Mic,
  MessageSquare,
  Webhook,
  Boxes,
  Braces,
  ChevronDown,
  Database,
  EyeOff,
  FileBarChart2,
  FileText,
  Globe,
  LayoutGrid,
  FlaskConical,
  Monitor,
  Network,
  Package,
  Plus,
  Redo2,
  Scaling,
  Search,
  Server,
  Settings,
  Smartphone,
  Table2,
  Tablet,
  Trash2,
  Undo2,
  Users,
  Variable,
  Waypoints,
  Zap,
} from "lucide-react";

import { type Breakpoint, isHiddenAtBreakpoint } from "@/lib/pageStyle";

import { PageRenderer, type EditCtx, type DropPos } from "@/components/portal/PageRenderer";
import { LibraryCodeContext } from "@/components/portal/CustomComponentFrame";
import { PortalShell } from "@/components/portal/PortalShell";
import { PortalSettingsModal } from "@/components/apps/PortalSettingsModal";
import { AppStructureMap } from "@/components/apps/AppStructureMap";
import {
  BuilderConsole,
  type ConsoleResponse,
} from "@/components/apps/pageBuilder/BuilderConsole";
import { QueryEditorPane } from "@/components/apps/pageBuilder/QueryEditorPane";
import { QueryListPanel } from "@/components/apps/pageBuilder/QueryListPanel";
import { JsObjectEditorPane } from "@/components/apps/pageBuilder/JsObjectEditorPane";
import { JsObjectListPanel } from "@/components/apps/pageBuilder/JsObjectListPanel";
import { GlobalStateEditor } from "@/components/apps/pageBuilder/GlobalStateEditor";
import { JsLibrariesPane, NEW as JS_LIB_NEW } from "@/components/apps/pageBuilder/JsLibrariesPane";
import { CustomComponentsPane } from "@/components/apps/pageBuilder/CustomComponentsPane";
import { AppQueriesPane } from "@/components/apps/pageBuilder/AppQueriesPane";
import { AppTablesPane } from "@/components/apps/pageBuilder/AppTablesPane";
import { AppDatasetsPane } from "@/components/apps/pageBuilder/AppDatasetsPane";
import { LinkedResourcesPane } from "@/components/apps/LinkedResourcesPane";
import { AppFormsPane } from "@/components/apps/pageBuilder/AppFormsPane";
import { AppAccessSections } from "@/pages/AppAccessPage";
import { InterfacesSection } from "@/components/apps/InterfacesSection";
import { RestApiDocs, RestInterfaceEditor } from "@/components/apps/RestInterfaceEditor";
import { GraphqlInterfaceEditor } from "@/components/apps/GraphqlInterfaceEditor";
// ADR-0058 — GraphiQL is a HEAVY dep (the `graphiql` bundle + its CSS), so the
// playground is lazy-loaded: its chunk ships only when the tab is opened.
const GraphqlPlayground = lazy(() => import("@/components/apps/GraphqlPlayground"));
import { ScheduledRulesSection } from "@/components/apps/ScheduledRulesSection";
import { AppAutomationsSection } from "@/components/apps/AppAutomationsSection";
import { PageInputsEditor } from "@/components/apps/pageBuilder/PageInputsEditor";
import { StateEditor } from "@/components/apps/pageBuilder/StateEditor";
import {
  WorkspaceTabs,
  type WorkspaceTabView,
} from "@/components/apps/pageBuilder/WorkspaceTabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { paletteIcon } from "@/components/apps/pageBuilder/paletteIcons";
import { PropsInspector } from "@/components/apps/pageBuilder/PropsInspector";
import { NodeIdField } from "@/components/apps/pageBuilder/NodeIdField";
import { NodeUsageSummary } from "@/components/apps/pageBuilder/NodeUsageSummary";
import { StylePanel } from "@/components/apps/pageBuilder/StylePanel";
import { VisibilityEditor } from "@/components/apps/pageBuilder/VisibilityEditor";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";
import { ApiError } from "@/lib/api";
import { appsApi, type AppGlobalStateEntry, type PageOut } from "@/lib/api-apps";
import { portalApi, type PortalPageResult } from "@/lib/api-portal";
import { columnValueType, inferValueType, type ValueType } from "@/lib/conditionExpr";
import { effectiveQueryRef } from "@/lib/dataBinding";
import { collectGlobalKeys } from "@/lib/pageActions";
import { reportTypesApi } from "@/lib/api-reports";
import { compileJsObjectsCached } from "@/lib/pageJsObjects";
import { useAppLibraries } from "@/lib/pageLibraries";
import { buildPageBindingsDts, buildPreviewScope, previewTemplate } from "@/lib/pagePreview";
import { consumerLabels, findReferences } from "@/lib/pageReferences";
import { computeRuntimeParams } from "@/lib/pageResourceParams";
import {
  UI_TAB,
  closeTab,
  nextActiveKey,
  openTab,
  pruneTabs,
  recallTabs,
  type TabSnapshot,
  type WorkspaceTab,
} from "@/lib/pageBuilder/workspace";
import { lintPage } from "@/lib/pageBuilder/lint";
import { captureConsole, nowHms, type LogEntry } from "@/lib/pageBuilder/runLog";
import type { PageAst, PageComponentSpec, PageNode, PageQuery } from "@/lib/pageTypes";
import {
  addChild,
  collectIds,
  dropSideBySide,
  duplicateNode,
  findNode,
  findParent,
  genId,
  isHorizontalContainer,
  insertChildAt,
  moveNode,
  moveRelative,
  moveSibling,
  newNode,
  reaimTabsDrop,
  removeNode,
  renameJsObjectRefs,
  renameNodeId,
  setVisibleWhen,
  shouldAdoptServerCopy,
  updateProps,
  updateStyle,
  validateNodeId,
} from "@/lib/pageTree";
import { resolvePageInputs } from "@/lib/pageInputs";

const ACCESS = ["restricted", "public"] as const;

/** Seed body for a freshly-added page JS Object (WS-2). */
/** ADR-0048 — the CORE (backend) context's rail areas: data · logic · access.
 *  Everything else is INTERFACCE (the shells over the app). Used to partition
 *  the rail by context and to keep the active area in sync when switching. */
const CORE_AREA_KEYS = new Set<string>([
  "appTables",
  "appDatasets",
  "appLinkedDatasets",
  "appLinkedKbs",
  "appLinkedReports",
  "appQueries",
  "appScheduledRules",
  "appAutomations",
  "appMembers",
]);

const JS_OBJECT_STARTER =
  "export default {\n" +
  "  // example(row) {\n" +
  "  //   return (row.first || '') + ' ' + (row.last || '');\n" +
  "  // },\n" +
  "};\n";

/** The page-query name a node's INSPECTOR uses for its design-time column aids
 *  (column dropdowns + "load values from data"). Data widgets bind their query
 *  via the `data` binding (ADR-0044 U1, the ONLY data source); the `page_filter`
 *  is the exception — it carries its own `query_ref` prop purely as a design-time
 *  pointer to whose columns the dropdowns should offer (its runtime filter is
 *  broadcast to every query). */
function designTimeQueryRef(node: PageNode): string {
  if (node.type === "page_filter") return String(node.props.query_ref ?? "");
  return effectiveQueryRef(node.props);
}

/** Map the viewport-preview selector to a responsive breakpoint, so the builder
 *  can grey out nodes the live portal would hide at that width (S15b). Mirrors
 *  the Tailwind breakpoints baked into RESPONSIVE_BOOLS: mobile <640, tablet
 *  640–1023, desktop ≥1024. A custom width is bucketed by its numeric pixels
 *  (a `%`/non-px value → desktop, the full-canvas case). */
function previewBreakpoint(
  viewport: "desktop" | "tablet" | "mobile" | "custom",
  customW: string,
): "mobile" | "tablet" | "desktop" {
  if (viewport === "mobile") return "mobile";
  if (viewport === "tablet") return "tablet";
  if (viewport === "desktop") return "desktop";
  // custom: only a plain px width buckets; "100%" / "auto" → full → desktop.
  const m = /^(\d+)\s*px$/i.exec(customW.trim()) ?? /^(\d+)$/.exec(customW.trim());
  if (!m) return "desktop";
  const px = parseInt(m[1], 10);
  if (px < 640) return "mobile";
  if (px < 1024) return "tablet";
  return "desktop";
}

export function PageBuilderPage() {
  const { appId = "", pageId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  // ADR-0049 Slice C — a PAGE-LESS builder: an app may have no web page (it's a
  // pure chat/voce/form app). The route `/apps/:appId/builder` carries no pageId;
  // the `?iface=` param says which interface to land on. The web canvas + page
  // selector simply don't apply (Slice B already hides them off the web interface).
  const pageless = !pageId;
  const ifaceParam = searchParams.get("iface"); // chat | voice | form | core | null
  // The area to land on for a page-less builder (the web `ui` canvas needs a page).
  const initialArea: string = !pageless
    ? "ui"
    : ifaceParam === "form"
      ? "appForms"
      : ifaceParam === "core"
        ? "appTables"
        : ifaceParam === "voice"
          ? "appInterfaceVoice"
          : "appInterfaceChat";

  const pageQ = useQuery({
    queryKey: ["page", appId, pageId],
    queryFn: () => appsApi.getPage(appId, pageId),
    enabled: !!pageId,
  });
  const catalogQ = useQuery({ queryKey: ["page-catalog"], queryFn: () => appsApi.pageCatalog() });
  // Canonical keys (audit 2026-07-02): share the SAME cache as AppTablesPane/AppFormsPane so a
  // table/form created in the builder invalidates these too (was `["tables"|"forms", appId]` →
  // the builder's own pickers stayed stale on a create/rename).
  const formsQ = useQuery({ queryKey: ["apps", appId, "forms"], queryFn: () => appsApi.listForms(appId) });
  const tablesQ = useQuery({ queryKey: ["apps", appId, "tables"], queryFn: () => appsApi.listTables(appId) });
  const datasetsQ = useQuery({
    queryKey: ["apps", appId, "datasets"],
    queryFn: () => appsApi.listDatasets(appId),
  });
  // App pages — the row-action target-page picker (S5) lists these.
  const pagesQ = useQuery({ queryKey: ["apps", appId, "pages"], queryFn: () => appsApi.listPages(appId) });
  // Target pages WITH their resolved `$input` contract (ADR-0045 R4-inputs), so
  // the `page_ref` params editor renders a guided form per picked page. The full
  // AST already rides on the list response, so resolution is local + cheap.
  const pageTargets = useMemo(
    () =>
      (pagesQ.data ?? []).map((p) => ({
        slug: p.slug,
        title: p.title,
        inputs: resolvePageInputs(p.ast),
      })),
    [pagesQ.data],
  );
  // App detail — its slug feeds the in-context portal-shell preview.
  const appQ = useQuery({ queryKey: ["apps", appId, "detail"], queryFn: () => appsApi.get(appId) });
  // ADR-0046 — load the app's external JS libraries so the canvas + the inspector
  // preview can resolve `{{ _.sum(...) }}` and compile JS Objects that use them.
  const appLibraries = useAppLibraries(appQ.data?.js_libraries);
  // Runnable workflows (S14) — app workflows with a `button_clicked` trigger,
  // via the ADMIN route (the portal twin is member-gated: without a portal
  // session it 401s silently and the picker rendered empty — W5 live bug).
  // Feeds the run-workflow action picker.
  const runnableQ = useQuery({
    queryKey: ["apps", appId, "button-workflows"],
    queryFn: () => appsApi.listButtonWorkflows(appId!),
    enabled: !!appId,
  });

  // Custom-component library (ADR-0022 #2) — feeds the `custom_component`
  // library picker AND the canvas resolver (a `source="library"` node renders
  // its referenced code live; the portal injects it server-side, the builder
  // looks it up here).
  const customComponentsQ = useQuery({
    queryKey: ["apps", appId, "custom-components"],
    queryFn: () => appsApi.listCustomComponents(appId),
  });
  // Named app-level Queries this page can REFERENCE (ADR-0047 convergence).
  const appQueriesQ = useQuery({
    queryKey: ["apps", appId, "queries"],
    queryFn: () => appsApi.listQueries(appId),
  });
  // `componentId → {code, mode}` for the canvas resolver (CustomComponentFrame
  // reads it via LibraryCodeContext to render a `source="library"` node live —
  // the mode picks the React vs HTML/CSS/JS host, ADR-0046).
  const libraryCodeMap = useMemo(
    () =>
      new Map(
        (customComponentsQ.data ?? []).map((c) => [c.id, { code: c.code, mode: c.mode }] as const),
      ),
    [customComponentsQ.data],
  );

  const [ast, setAst] = useState<PageAst | null>(null);
  const [settings, setSettings] = useState<Pick<PageOut, "title" | "slug" | "access" | "is_home" | "show_in_nav" | "overlay" | "overlay_mode">>();
  const [selectedId, setSelectedId] = useState<string>("root");
  const [error, setError] = useState<string | null>(null);
  // Responsive preview: constrains the canvas width to a device size so you
  // can check how the page reflows on tablet / phone.
  const [viewport, setViewport] = useState<"desktop" | "tablet" | "mobile" | "custom">("desktop");
  // ADR-0045 R3 — running canvas: when on, the canvas renders as the LIVE portal
  // over a sandbox runtime (buttons run, modals open) instead of the editing
  // chrome. Navigation + server actions are gated (preview policy).
  // Custom viewport: free width/height (any CSS value, e.g. "100%", "480px").
  const [customW, setCustomW] = useState("100%");
  const [customH, setCustomH] = useState("auto");
  // Show the page inside the real portal shell (header/nav/footer) so you
  // design in context. The chrome is non-interactive here (edit it in Portal
  // settings); toggle off for a focused "just the page" view.
  const [showShell, setShowShell] = useState(true);
  // Left panel is tabbed (Webflow-style icon rail) so the 5 sections no longer
  // stack into one endless scroll — one panel is visible at a time.
  // WS-6 — the sub-toggle INSIDE the UI area: the component palette vs the
  // layers outline (the old separate `insert`/`layers` rail icons).
  const [uiPanel, setUiPanel] = useState<"insert" | "layers">("insert");
  // WS-7 (owner UX pass) — the rail is now a pure AREA switcher (every icon =
  // a top tab); there's no separate `leftTab`. The Query / JS areas are
  // master/detail: a list (the area's own sidebar) + the SELECTED editor. These
  // hold which query/object is open inside those areas.
  const [selectedQueryId, setSelectedQueryId] = useState<string | null>(null);
  const [selectedJsName, setSelectedJsName] = useState<string | null>(null);
  // WS-8 — every non-UI area is master/detail like Query: the LIST lives in the
  // lateral sidebar and the DETAIL in the content, so the editor mounts twice
  // (list + detail) and the selection must be LIFTED here to keep them in sync.
  const [selectedVarId, setSelectedVarId] = useState<string | null>(null);
  const [selectedGlobalIdx, setSelectedGlobalIdx] = useState<number>(0);
  const [selectedLibName, setSelectedLibName] = useState<string>(JS_LIB_NEW);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  // ADR-0047 — the selected app Query (or the `__api__` docs sentinel) in the
  // rail-bottom "Query app" area (lifted so the list + detail mounts agree).
  const [selectedAppQueryId, setSelectedAppQueryId] = useState<string | null>(null);
  const [selectedRestEndpointId, setSelectedRestEndpointId] = useState<string | null>(null);
  // ADR-0051 — REST Docs: the endpoint anchor picked in the sidebar nav, so the
  // reference body (content) scrolls to it (list/detail split, standard sidebar).
  const [selectedRestDocAnchor, setSelectedRestDocAnchor] = useState<string | null>(null);
  // ADR-0058 — GraphQL: the selected resource (or the `__schema__` settings
  // sentinel) in the rail-bottom "GraphQL" area (list + detail mounts agree).
  const [selectedGraphqlResourceId, setSelectedGraphqlResourceId] = useState<string | null>(null);
  // ADR-0048 single-IDE — the selected data table in the rail-bottom "Tabelle"
  // area (lifted so the list sidebar + detail content mounts agree).
  const [selectedAppTableId, setSelectedAppTableId] = useState<string | null>(null);
  const [selectedAppDatasetId, setSelectedAppDatasetId] = useState<string | null>(null);
  // ADR-0059 — selected LINKED dataset/KB (master/detail Core areas). `__add__` = picker open.
  const [selectedLinkedDatasetId, setSelectedLinkedDatasetId] = useState<string | null>(null);
  const [selectedLinkedKbId, setSelectedLinkedKbId] = useState<string | null>(null);
  const [selectedLinkedReportId, setSelectedLinkedReportId] = useState<string | null>(null);
  const [selectedAppFormId, setSelectedAppFormId] = useState<string | null>(null);
  // ADR-0048 — the IDE splits into TWO declared contexts (owner's "stacca
  // backend dal frontend"): INTERFACCE (the shells over the app — web app pages,
  // assistant, forms, frontend resources) and CORE (data · logic · access). The
  // rail shows only the active context's areas; the toggle swaps the whole shell.
  const [builderContext, setBuilderContext] = useState<"interfacce" | "core" | "mappa">(
    pageless && ifaceParam === "core" ? "core" : "interfacce",
  );
  // WS-6 — the per-page metadata form (Title/Slug/Access/…) moved OFF the header
  // into a dialog opened from the breadcrumb page menu.
  const [pageSettingsOpen, setPageSettingsOpen] = useState(false);
  // Workspace tabs (WS-1): the main pane is context-routed — the UI canvas plus
  // any opened Query/JS/Stato editors live as persistent tabs. Page-scoped:
  // switching page resets the set to just `ui`.
  const [openTabs, setOpenTabs] = useState<WorkspaceTab[]>([
    pageless ? ({ kind: initialArea } as WorkspaceTab) : UI_TAB,
  ]);
  const [activeKey, setActiveKey] = useState<string>(initialArea);
  // WS-5 — workspace tabs are remembered PER PAGE: switching back to a page
  // restores its open editors instead of always resetting to the UI tab.
  // `liveTabsRef` always holds the current snapshot (read when leaving a page);
  // `tabMemoryRef` is the per-page store; `prevPageIdRef` the outgoing page.
  const tabMemoryRef = useRef(new Map<string, TabSnapshot>());
  const liveTabsRef = useRef<TabSnapshot>({ openTabs, activeKey });
  liveTabsRef.current = { openTabs, activeKey };
  const prevPageIdRef = useRef(pageId);
  // WS-5 — the Console Response tab: the last explicit run (query preview / JS
  // tester). `responseNonce` bumps per run so the Console focuses Response.
  const [lastResponse, setLastResponse] = useState<ConsoleResponse | null>(null);
  const [responseNonce, setResponseNonce] = useState(0);
  // WS-4b — APP-GLOBAL $global declared bag (apps.global_state). Local editable
  // copy seeded once per app; persisted DEBOUNCED via an app PATCH. The handlers
  // (onGlobalStateChange / openGlobalStateTab / openSettingsTab) live further
  // down with the other tab helpers; the HOOKS must stay here, above any early
  // return.
  const [globalState, setGlobalState] = useState<AppGlobalStateEntry[]>([]);
  const saveGlobalState = useMutation({
    mutationFn: (gs: AppGlobalStateEntry[]) => appsApi.update(appId, { global_state: gs }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId, "detail"] }),
  });
  const gsSaveTimer = useRef<ReturnType<typeof setTimeout>>();
  const appGlobalState = appQ.data?.global_state;
  useEffect(() => {
    // Re-seed only when the APP changes (id stable across refetches), so a
    // post-save refetch never clobbers in-flight edits.
    setGlobalState(appGlobalState ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appQ.data?.id]);
  // Builder Console run logs (WS-3b): captured console output + run events from
  // manual JS-function / query-preview runs. Page-scoped (reset on page change).
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const logSeq = useRef(0);
  // Bottom Data dock (query authoring): full-width, resizable. Height persisted
  // so the author doesn't re-drag every session.
  const [dataDockOpen, setDataDockOpen] = useState(false);
  const [dataDockHeight, setDataDockHeightRaw] = useState<number>(() => {
    if (typeof window === "undefined") return 300;
    const n = Number.parseInt(window.localStorage.getItem("tray.pageBuilder.dataDockHeight") ?? "", 10);
    return Number.isFinite(n) && n > 0 ? n : 300;
  });
  const setDataDockHeight = useCallback((h: number) => {
    setDataDockHeightRaw(h);
    if (typeof window !== "undefined")
      window.localStorage.setItem("tray.pageBuilder.dataDockHeight", String(Math.round(h)));
  }, []);
  // Portal settings modal (branding / security / navigation) — opened from the
  // toolbar; the editor is the single hub for app-interface configuration.
  // Create-page dialog (ADR-0023): pick ordinary page vs overlay (modal/drawer).
  const [createOpen, setCreateOpen] = useState(false);
  // Free-text filter over the Insert palette (the component set keeps growing).
  const [paletteQuery, setPaletteQuery] = useState("");
  // Inspector is tabbed too (S15): Properties (per-component props) vs Style
  // (shared style tokens, every component accepts them).
  const [inspectorTab, setInspectorTab] = useState<"props" | "style">("props");
  // Undo/redo: immutable AST snapshots. `past`/`future` are stacks of whole
  // ASTs; every structural mutation pushes onto `past` and clears `future`.
  const [past, setPast] = useState<PageAst[]>([]);
  const [future, setFuture] = useState<PageAst[]>([]);
  // Keyboard shortcuts read the latest action handlers through a ref (the
  // listener is bound once, before the loading early-return).
  const kbdRef = useRef<{
    undo: () => void;
    redo: () => void;
    del: () => void;
    dup: () => void;
    esc: () => void;
    save: () => void;
  } | null>(null);

  // Insert-at-cursor bridge: the focused expression field registers its insert
  // callback here; the Data panel calls `insertBinding` to drop a `{{ … }}`
  // binding at the caret (falling back to copy when nothing is focused).
  // Stable identities (defined before the loading guard) so the registration
  // effect in ExpressionField doesn't churn.
  const activeInsertRef = useRef<((text: string) => void) | null>(null);
  const registerInsert = useCallback((insert: ((text: string) => void) | null) => {
    activeInsertRef.current = insert;
  }, []);
  const insertBinding = useCallback((expr: string) => {
    if (!activeInsertRef.current) return false;
    activeInsertRef.current(expr);
    return true;
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const h = kbdRef.current;
      if (!h) return;
      const t = e.target as HTMLElement | null;
      // Don't hijack keys while the user types in the inspector / a field.
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable))
        return;
      const mod = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();
      if (mod && k === "z") { e.preventDefault(); e.shiftKey ? h.redo() : h.undo(); }
      else if (mod && k === "y") { e.preventDefault(); h.redo(); }
      else if (mod && k === "s") { e.preventDefault(); h.save(); }
      else if (mod && k === "d") { e.preventDefault(); h.dup(); }
      else if (e.key === "Escape") { h.esc(); }
      else if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); h.del(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Switching page (Pages sidebar) keeps this component mounted with a new
  // :pageId — reset the local AST so the seeding effect re-hydrates it.
  useEffect(() => {
    setAst(null);
    setSettings(undefined);
    setSelectedId("root");
    setPast([]);
    setFuture([]);
    // WS-5 — workspace tabs are remembered PER PAGE: save the OUTGOING page's
    // open editors, then restore the INCOMING page's (default = just the UI
    // tab). `liveTabsRef` still holds the outgoing page's tabs here (this effect
    // runs before the restore setState commits).
    const prev = prevPageIdRef.current;
    if (prev && prev !== pageId) tabMemoryRef.current.set(prev, liveTabsRef.current);
    // ADR-0049 Slice C — a page-less builder has no per-page tab memory; land on
    // the requested interface area instead of the web `ui` default.
    const snap = pageless
      ? { openTabs: [{ kind: initialArea } as WorkspaceTab], activeKey: initialArea }
      : recallTabs(tabMemoryRef.current, pageId);
    setOpenTabs(snap.openTabs);
    setActiveKey(snap.activeKey);
    prevPageIdRef.current = pageId;
    setLogs([]);
    setLastResponse(null);
    setResponseNonce(0);
    // Keyed on the page identity only — `pageless`/`initialArea` are stable for a
    // given mount (route-derived), so they don't belong in the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  // Keep the open tabs honest: the Query / JS AREA tabs close when the page has
  // none left. `pruneTabs` returns the same array when nothing changed, so this
  // is a no-op render in the common case.
  useEffect(() => {
    if (!ast) return;
    const hasQueries = ast.queries.length > 0;
    const hasJs = (ast.js_objects ?? []).length > 0;
    setOpenTabs((tabs) => pruneTabs(tabs, hasQueries, hasJs));
  }, [ast]);

  // Snapshot of the last SAVED state (ast + settings), to detect unsaved edits.
  const savedSnapshot = useRef<string>("");
  useEffect(() => {
    if (pageQ.data && !ast) {
      const loaded = {
        title: pageQ.data.title,
        slug: pageQ.data.slug,
        access: pageQ.data.access,
        is_home: pageQ.data.is_home,
        show_in_nav: pageQ.data.show_in_nav,
        overlay: pageQ.data.overlay,
        overlay_mode: pageQ.data.overlay_mode,
      };
      setAst(pageQ.data.ast);
      setSettings(loaded);
      savedSnapshot.current = JSON.stringify({ ast: pageQ.data.ast, settings: loaded });
    } else if (pageless && !ast) {
      // ADR-0049 Slice C — page-less builder: seed a COMPLETE empty page AST so the
      // shell renders (the gate needs ast+settings). The web canvas is never shown
      // here (Slice B hides it off the web interface), and editing is page-less +
      // inert (dirty/save are disabled below), so this AST is never persisted.
      const blank: PageAst = {
        root: { id: "root", type: "page", props: {}, children: [] } as PageAst["root"],
        queries: [],
        variables: [],
      };
      setAst(blank);
      setSettings({
        title: "",
        slug: "",
        access: "public",
        is_home: false,
        show_in_nav: false,
        overlay: false,
        overlay_mode: "modal",
      });
      savedSnapshot.current = "__pageless__"; // never matches → see the dirty guard
    }
  }, [pageQ.data, ast, pageless]);

  // Re-hydrate when THIS page changed on the SERVER under us — i.e. Odino edited
  // the very page being viewed through its own session, then the live-refresh
  // invalidated ["page", …] and it refetched (ADR-0024 slice D). The builder
  // holds a working COPY of the ast in local state, so a query refetch alone
  // never reaches the canvas; we adopt the server copy here. Guarded on `!dirty`
  // so we never clobber unsaved local edits — and the edit-lock keeps that
  // window clean during an Odino turn. Undo history resets (it's a fresh load).
  useEffect(() => {
    if (!pageQ.data || !ast) return; // initial load handled by the seeding effect
    if (!shouldAdoptServerCopy(pageQ.data.ast, ast, dirty)) return;
    const loaded = {
      title: pageQ.data.title,
      slug: pageQ.data.slug,
      access: pageQ.data.access,
      is_home: pageQ.data.is_home,
      show_in_nav: pageQ.data.show_in_nav,
      overlay: pageQ.data.overlay,
      overlay_mode: pageQ.data.overlay_mode,
    };
    setAst(pageQ.data.ast);
    setSettings(loaded);
    setSelectedId("root");
    setPast([]);
    setFuture([]);
    savedSnapshot.current = JSON.stringify({ ast: pageQ.data.ast, settings: loaded });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageQ.dataUpdatedAt]);

  // Unsaved-changes guard (#3): dirty when the live ast/settings differ from the
  // last saved snapshot. We block both tab-close/reload (beforeunload) and
  // in-app route changes (router useBlocker) so work isn't silently lost.
  // ADR-0049 Slice C — a page-less builder edits no page, so it's never dirty
  // (the web canvas is hidden; Save is disabled below).
  const dirty = !pageless && ast != null && savedSnapshot.current !== JSON.stringify({ ast, settings });
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);
  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: { pathname: string };
        nextLocation: { pathname: string };
      }) => dirty && currentLocation.pathname !== nextLocation.pathname,
      [dirty],
    ),
  );
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm("You have unsaved changes. Leave without saving?")) blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  const catalog = catalogQ.data ?? [];
  const specByType = useMemo(
    () => new Map(catalog.map((c) => [c.type, c] as const)),
    [catalog],
  );

  const save = useMutation({
    mutationFn: () =>
      appsApi.updatePage(appId, pageId, {
        ast: ast!,
        title: settings!.title,
        slug: settings!.slug,
        access: settings!.access,
        is_home: settings!.is_home,
        show_in_nav: settings!.show_in_nav,
        overlay: settings!.overlay,
        overlay_mode: settings!.overlay_mode,
      }),
    onSuccess: () => {
      setError(null);
      // Mark the just-saved state as clean so the unsaved-changes guard clears.
      savedSnapshot.current = JSON.stringify({ ast, settings });
      qc.invalidateQueries({ queryKey: ["page", appId, pageId] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.code : "save failed"),
  });

  // Create a blank page and open it (Pages sidebar "+ New"). The `kind` picks
  // an ordinary nav page vs an overlay page (ADR-0023) opened as a modal/drawer.
  const createPage = useMutation({
    mutationFn: (kind: "page" | "modal" | "drawer") => {
      // Slugs are `^[a-z][a-z0-9_]*$` (no hyphens) — and must be unique per app,
      // so step past any existing `page_N` (deleted pages can leave gaps).
      const existing = new Set((pagesQ.data ?? []).map((p) => p.slug));
      let n = (pagesQ.data?.length ?? 0) + 1;
      while (existing.has(`page_${n}`)) n += 1;
      const overlay = kind !== "page";
      return appsApi.createPage(appId, {
        slug: `page_${n}`,
        title: `Page ${n}`,
        overlay,
        // An overlay page defaults to hidden-from-nav (it's not a route).
        ...(overlay ? { overlay_mode: kind as "modal" | "drawer", show_in_nav: false } : {}),
      });
    },
    onSuccess: (p) => {
      setCreateOpen(false);
      qc.invalidateQueries({ queryKey: ["apps", appId, "pages"] });
      navigate(`/apps/${appId}/pages/${p.id}`);
    },
    onError: (e) => setError(e instanceof ApiError ? e.code : "create failed"),
  });

  // Delete a page from the editor (the app screen no longer lists pages). When
  // the open page is the one deleted, jump to another remaining page, or back
  // to the app if it was the last.
  const deletePage = useMutation({
    mutationFn: (id: string) => appsApi.deletePage(appId, id),
    onSuccess: (_void, deletedId) => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "pages"] });
      if (deletedId === pageId) {
        const next = (pagesQ.data ?? []).find((p) => p.id !== deletedId);
        navigate(next ? `/apps/${appId}/pages/${next.id}` : `/apps/${appId}`);
      }
    },
    onError: (e) => setError(e instanceof ApiError ? e.code : "delete failed"),
  });

  // Preview: fetch rows for every declared query (admin view, no RLS).
  const previewQ = useQuery({
    queryKey: ["page-preview", appId, JSON.stringify(ast?.queries ?? [])],
    enabled: Boolean(ast),
    queryFn: async () => {
      const out: Record<string, { rows: Record<string, unknown>[]; total: number; page: number; page_size: number }> = {};
      for (const q of ast?.queries ?? []) {
        try {
          if (q.source_kind === "dataset" && q.dataset_id) {
            // Dataset-backed query: preview via the compiled read model.
            const res = await appsApi.previewDataset(appId, q.dataset_id, {
              page_size: q.page_size ?? 50,
            });
            out[q.name] = { rows: res.rows, total: res.total, page: res.page, page_size: res.page_size };
          } else if (q.aggregate && q.aggregate.metrics.length > 0) {
            // Aggregate queries return groups under `rows` (uniform consumption
            // by stat/chart/expressions) — mirror the runtime's "groups-as-rows".
            const res = await appsApi.aggregate(appId, q.table_id ?? "", {
              metrics: q.aggregate.metrics,
              group_by: q.aggregate.group_by ?? [],
            });
            out[q.name] = { rows: res.groups, total: res.groups.length, page: 1, page_size: res.groups.length };
          } else {
            const res = await appsApi.queryRows(appId, q.table_id ?? "", { page_size: q.page_size ?? 50 });
            out[q.name] = { rows: res.rows, total: res.total, page: res.page, page_size: res.page_size };
          }
        } catch {
          out[q.name] = { rows: [], total: 0, page: 1, page_size: 50 };
        }
      }
      return out;
    },
  });

  // For a selected `record` block, its column picker should offer the bound
  // table's columns (the record edits a table directly — S5d). Fetch them
  // when a record with a table_id is selected.
  const selectedNode = ast ? findNode(ast.root, selectedId) : null;
  const recordTableId =
    selectedNode?.type === "record" ? String(selectedNode.props.table_id ?? "") : "";
  const recordTableQ = useQuery({
    queryKey: ["apps", appId, "tables", recordTableId],
    queryFn: () => appsApi.getTable(appId, recordTableId),
    enabled: !!recordTableId,
  });

  // Columns for every table (S13) — feeds the action-chain field-values
  // column picker (no more free-text column names). One fetch per table,
  // cached; small N (apps have a handful of tables).
  const tableIds = (tablesQ.data ?? []).map((t) => t.id);
  const columnsByTableQ = useQuery({
    queryKey: ["tables-columns", appId, tableIds.join(",")],
    enabled: tableIds.length > 0,
    queryFn: async () => {
      const names: Record<string, string[]> = {};
      // colName → condition ValueType, from the real column schema (A1) — the
      // type chip's default, correct even with no preview rows.
      const types: Record<string, Record<string, ValueType>> = {};
      const details = await Promise.all(tableIds.map((id) => appsApi.getTable(appId, id)));
      details.forEach((d) => {
        const cols = (d.columns ?? []).filter((c) => c.kind !== "system");
        names[d.id] = cols.filter((c) => c.kind === "data").map((c) => c.name);
        types[d.id] = Object.fromEntries(
          cols.map((c) => [c.name, columnValueType(c.data_type, c.kind)]),
        );
      });
      return { names, types };
    },
  });

  // Static "where used" index (forward + inverse) — powers the Data dock's
  // delete-guard, usage badges + the inspector's inverse summary. Computed BEFORE
  // the loading early-return so the hook order stays stable across renders
  // (`ast` is null until loaded → guard, never gate the hook on it).
  const pageRefs = useMemo(() => (ast ? findReferences(ast) : undefined), [ast]);
  // Page-wide lint (WS-3b): JS Objects + action JS + {{ }} bindings — feeds the
  // Console's Linter tab. Memoised on the ast so it doesn't re-run per keystroke.
  const lintIssues = useMemo(() => (ast ? lintPage(ast) : []), [ast]);

  // Overlay pages (ADR-0023) for the `open_overlay` action picker.
  const overlayPages = useMemo(
    () =>
      (pagesQ.data ?? [])
        .filter((p) => p.overlay)
        .map((p) => ({
          slug: p.slug,
          title: p.title,
          mode: p.overlay_mode,
          // The overlay's resolved `$input` contract (ADR-0045 R4-inputs) — the
          // open_overlay context editor renders a guided form from it.
          inputs: resolvePageInputs(p.ast),
        })),
    [pagesQ.data],
  );
  // `$input.<key>` references already used in THIS overlay page — surfaced in the
  // bindings explorer (the keys themselves come from the opener's context at
  // runtime, so they can't be enumerated up-front).
  const inputKeys = useMemo(() => {
    if (!ast) return [];
    const found = new Set<string>();
    const re = /\$input\.([a-zA-Z_$][\w$]*)/g;
    let m: RegExpExecArray | null;
    const src = JSON.stringify(ast);
    while ((m = re.exec(src))) found.add(m[1]);
    return [...found].sort();
  }, [ast]);
  // In-page modal/drawer COMPONENTS on this page — power the open_modal /
  // close_modal action target picker (ADR-0045 R2). Walks the AST for the new
  // overlay-container kinds; label = its title (or kind) + a short id hint.
  const modalTargets = useMemo(() => {
    const out: { id: string; label: string }[] = [];
    const walk = (n: { id: string; type: string; props?: Record<string, unknown>; children?: unknown[] } | undefined) => {
      if (!n) return;
      if (n.type === "modal" || n.type === "drawer") {
        const t = n.props?.title;
        const title = typeof t === "string" && t.trim() ? t.trim() : n.type;
        out.push({ id: n.id, label: `${title} · ${n.id.slice(0, 6)}` });
      }
      for (const c of (n.children ?? []) as typeof n[]) walk(c);
    };
    if (ast) walk(ast.root as Parameters<typeof walk>[0]);
    return out;
  }, [ast]);

  // ADR-0060 S5 — the reports of the app's LINKED Types power the
  // `print_report` action picker (label = "Tipo · Report"). Cache-shared with
  // the Core "Report" area + the Reports page.
  const linkedReportTypesQ = useQuery({
    queryKey: ["apps", appId, "linked-report-types"],
    queryFn: () => appsApi.listLinkedReportTypes(appId),
  });
  const linkedReportTypes = linkedReportTypesQ.data ?? [];
  const reportListQs = useQueries({
    queries: linkedReportTypes.map((t) => ({
      queryKey: ["report-types", t.id, "reports"],
      queryFn: () => reportTypesApi.listReports(t.id),
    })),
  });
  const reportOptions = linkedReportTypes.flatMap((t, i) =>
    (reportListQs[i]?.data ?? []).map((r) => ({ id: r.id, label: `${t.name} · ${r.name}` })),
  );

  if (pageQ.isLoading || catalogQ.isLoading || !ast || !settings) {
    return <div className="p-6 text-sm text-muted-foreground">Loading builder…</div>;
  }

  const selected = findNode(ast.root, selectedId);
  const selectedSpec = selected ? specByType.get(selected.type) : undefined;

  // Page-state keys created implicitly by components — input `state_key`s +
  // data_list `select_into` vars. Surfaced under $page in the Data panel even
  // though they aren't declared in `ast.variables` (S13).
  const stateKeys: string[] = (() => {
    const out = new Set<string>();
    const walk = (n: PageNode) => {
      if (n.type.endsWith("_input")) {
        const k = String(n.props.state_key ?? "").trim();
        if (k) out.add(k);
      }
      if (n.type === "data_list") {
        const k = String(n.props.select_into ?? "").trim();
        if (k) out.add(k);
      }
      n.children?.forEach(walk);
    };
    walk(ast.root);
    return [...out];
  })();

  // `$global` keys the page WRITES (ADR-0043) — scanned from every node's action
  // chains so `{{ $global.<key> }}` autocompletes + previews in the inspector.
  const globalKeys: string[] = (() => {
    const out = new Set<string>();
    const walk = (n: PageNode) => {
      collectGlobalKeys(n.props, out);
      n.children?.forEach(walk);
    };
    walk(ast.root);
    return [...out];
  })();

  // When the selection sits inside (or is) a repeater, the Data panel can
  // offer `{{ $item.<col> }}` for that repeater's query columns, and the
  // expression preview can resolve `$item.<col>` against a real sample row.
  // Climb from the selection to the root looking for the nearest repeater.
  const repeater: { columns: string[]; sample?: Record<string, unknown>; tableId?: string } = (() => {
    let cur: string | undefined = selectedId;
    const seen = new Set<string>();
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      const node = findNode(ast.root, cur);
      if (node?.type === "repeater") {
        const ref = effectiveQueryRef(node.props);
        const rows = previewQ.data?.[ref]?.rows ?? [];
        const tableId = ast.queries.find((q) => q.name === ref)?.table_id;
        return { columns: rows.length > 0 ? Object.keys(rows[0]) : [], sample: rows[0], tableId };
      }
      cur = findParent(ast.root, cur)?.id;
    }
    return { columns: [] };
  })();
  const repeaterColumns = repeater.columns;

  // --- AST mutation through the undo/redo history -------------------------
  // Every structural change goes through `commitAst`, which snapshots the
  // previous AST onto `past` (capped) and clears the redo stack. Callers run
  // after the loading guard, so `ast` is non-null here.
  function commitAst(next: PageAst) {
    if (!ast || next === ast) return;
    setPast((p) => [...p, ast].slice(-50));
    setFuture([]);
    setAst(next);
  }
  function commitRoot(root: PageNode) {
    if (ast && root !== ast.root) commitAst({ ...ast, root });
  }
  function mutateNode(fn: (root: PageNode) => PageNode) {
    if (ast) commitRoot(fn(ast.root));
  }

  function undo() {
    if (past.length === 0 || !ast) return;
    const prev = past[past.length - 1];
    setPast((p) => p.slice(0, -1));
    setFuture((f) => [ast, ...f].slice(0, 50));
    setAst(prev);
    setSelectedId("root");
  }
  function redo() {
    if (future.length === 0 || !ast) return;
    const next = future[0];
    setFuture((f) => f.slice(1));
    setPast((p) => [...p, ast].slice(-50));
    setAst(next);
    setSelectedId("root");
  }

  function addComponent(spec: PageComponentSpec) {
    if (!ast) return;
    const used = collectIds(ast.root);
    const node = newNode(spec, used);
    // Add under the selected container, else under the root.
    const target =
      selected && specByType.get(selected.type)?.allows_children ? selected.id : ast.root.id;
    commitRoot(addChild(ast.root, target, node));
    setSelectedId(node.id); // select the freshly-added node
  }

  function duplicate(id: string) {
    const out: string[] = [];
    mutateNode((r) => duplicateNode(r, id, out));
    if (out[0]) setSelectedId(out[0]);
  }

  /**
   * Canvas drop with a precise insertion point (Elementor-style). `payload`
   * is an existing node id (reorder) or `new:<type>` (a palette drag).
   * `position` is relative to `targetId`: before/after = sibling, inside =
   * append into the target container.
   */
  function placeDrop(payload: string, targetId: string, position: DropPos) {
    if (!ast) return;
    let placedId = payload;
    let fresh: PageNode | null = null;
    let droppedType: string | undefined;
    if (payload.startsWith("new:")) {
      const spec = specByType.get(payload.slice(4));
      if (!spec) return;
      fresh = newNode(spec, collectIds(ast.root));
      placedId = fresh.id;
      droppedType = spec.type;
    } else {
      droppedType = findNode(ast.root, placedId)?.type;
    }

    // Horizontal side-band drop → wrap target + dropped into a `columns` grid
    // side by side (Elementor-style auto-wrap), or widen an existing columns.
    if (position === "left" || position === "right") {
      const used = collectIds(ast.root);
      if (fresh) used.add(fresh.id);
      const colsId = genId(used);
      const moving = fresh ?? findNode(ast.root, placedId);
      if (!moving) return;
      mutateNode((root) => dropSideBySide(root, targetId, moving, position, colsId));
      setSelectedId(placedId);
      return;
    }

    // Tabs invariant: re-aim a non-`tab` drop that would land directly under a
    // `tabs` node into the visible tab (see reaimTabsDrop). Prevents the common
    // "second component into a tab spawns a new tab" surprise.
    const aimed = reaimTabsDrop(ast.root, droppedType, targetId, position, selectedId);
    if (!aimed) return; // empty tabs: drop a Tab first.
    const dropId = aimed.targetId;
    const dropPos = aimed.position;

    mutateNode((root) => {
      if (dropPos === "inside") {
        const t = findNode(root, dropId);
        if (!t || !(specByType.get(t.type)?.allows_children ?? false)) return root;
        return fresh ? addChild(root, dropId, fresh) : moveNode(root, placedId, dropId, -1);
      }
      const parent = findParent(root, dropId);
      if (!parent) return root; // target is the root → it has no siblings
      if (fresh) {
        const idx = parent.children.findIndex((c) => c.id === dropId);
        return insertChildAt(root, parent.id, dropPos === "before" ? idx : idx + 1, fresh);
      }
      return moveRelative(root, placedId, dropId, dropPos);
    });
    setSelectedId(placedId);
  }

  // The WYSIWYG canvas selection chrome (select / move / duplicate / delete).
  const editing: EditCtx = {
    selectedId,
    onSelect: setSelectedId,
    onDelete: (id) => {
      mutateNode((r) => removeNode(r, id));
      if (selectedId === id) setSelectedId("root");
    },
    onMove: (id, dir) => mutateNode((r) => moveSibling(r, id, dir)),
    onDuplicate: duplicate,
    onDrop: placeDrop, // drag-reorder + palette-drop with an insertion line
    allowsChildren: (type) => specByType.get(type)?.allows_children ?? false,
    // A node sits in a horizontal container when its parent is a `container` in
    // row/grid layout — the insertion line then runs vertically (left/right).
    horizontalAt: (id) => isHorizontalContainer(findParent(ast.root, id)),
    labelFor: (type) => specByType.get(type)?.label ?? type,
    // S15b: grey out nodes the live portal would hide at the previewed width.
    // mobile<640, tablet 640–1023, desktop≥1024; a custom width maps by pixels.
    previewBreakpoint: previewBreakpoint(viewport, customW),
  };

  // Wire the latest action closures for the keyboard shortcut listener.
  kbdRef.current = {
    undo,
    redo,
    del: () => {
      if (selectedId !== "root") {
        mutateNode((r) => removeNode(r, selectedId));
        setSelectedId("root");
      }
    },
    dup: () => {
      if (selectedId !== "root") duplicate(selectedId);
    },
    esc: () => setSelectedId("root"),
    save: () => save.mutate(),
  };

  /**
   * Drag-drop reorder (P7c2): drop `dragId` onto `dropId`. If the drop
   * target is a container, the node becomes its last child; otherwise it
   * lands as the next sibling of the drop target. `moveNode` rejects any
   * illegal move (into self/descendant) so a bad drag is a no-op.
   */
  function dropOnNode(dragId: string, dropId: string) {
    if (dragId === dropId) return;
    mutateNode((root) => {
      const target = findNode(root, dropId);
      if (!target) return root;
      const isContainer = specByType.get(target.type)?.allows_children ?? false;
      if (isContainer) return moveNode(root, dragId, dropId, target.children.length);
      const parent = findParent(root, dropId);
      if (!parent) return root;
      const idx = parent.children.findIndex((c) => c.id === dropId);
      return moveNode(root, dragId, parent.id, idx + 1);
    });
  }

  const previewResult: PortalPageResult = {
    page: { id: pageId, slug: settings.slug, title: settings.title, access: settings.access, ast },
    queries: previewQ.data ?? {},
    user: { id: "preview", email: "preview@admin", full_name: "Preview" },
    role: null,
    // WS-4a — the app's declared $global so the canvas resolves `{{ $global.x }}`.
    globalVariables: globalState,
    // ADR-0046 — the app's external libraries; PageRenderer loads them (its own
    // `useAppLibraries`) so the canvas resolves `{{ _.sum(...) }}` + JS Objects.
    jsLibraries: appQ.data?.js_libraries,
  };

  // The editing canvas — one element reused in all three shell wrappers below.
  // (The R3 "Anteprima" running-canvas mode was removed — to be redesigned.)
  const canvasContent = <PageRenderer result={previewResult} editing={editing} />;

  // Expression aids for the inspector's `{{ }}` fields: a live scope ($user /
  // $role / $queries / $page / $item) → an ambient `.d.ts` for Monaco
  // IntelliSense + a preview evaluator that renders the result under each
  // field. Both flow into `ExpressionField` via `PropsInspector`. Cheap to
  // recompute per render; the evaluator only runs when a field calls it.
  const exprScope = buildPreviewScope({
    user: previewResult.user,
    role: previewResult.role,
    preview: previewQ.data ?? {},
    variables: ast.variables,
    stateKeys,
    // ADR-0043: every declared query name + the page's `$global` keys, so both
    // autocomplete in `{{ }}` fields before any preview data has loaded.
    queryNames: ast.queries.map((q) => q.name),
    globalKeys,
    // WS-4a — the app's DECLARED $global variables, so their initial values
    // preview + autocomplete (not just the action-written keys).
    globalVariables: globalState,
    // ADR-0044: the page's JS Objects — compiled into the preview scope so a
    // binding `{{ Utils.fn() }}` evaluates in the inspector + autocompletes.
    jsObjects: ast.js_objects ?? [],
    // ADR-0046: the app's external libraries (loaded) so `{{ _.sum(...) }}`
    // previews in the inspector + JS Objects using them compile cleanly.
    libraries: appLibraries,
    // R4-inputs: the page's declared/discovered `$input` params, so `{{ $input. }}`
    // autocompletes the names of the parameters this page expects.
    inputKeys: resolvePageInputs(ast).map((i) => i.name),
    itemColumns: repeaterColumns,
    itemSample: repeater.sample,
  });
  // ADR-0046 Slice 4 — declare the app's library globals so `{{ _.sumBy(…) }}` /
  // `dayjs(…).format(…)` autocomplete in every binding + JS editor.
  const exprAmbientDts = buildPageBindingsDts(exprScope, appQ.data?.js_libraries);
  const previewExpr = (tpl: string) => previewTemplate(tpl, exprScope);

  // ── Workspace tabs (WS-1) ────────────────────────────────────────────────
  // The `$page` var names the QueryForm `deps` picker offers: declared vars +
  // component-written keys.
  const pageVarNames = [...new Set([...ast.variables.map((v) => v.name), ...stateKeys])];
  const canAddQuery = (tablesQ.data ?? []).length > 0;
  // Open the Query AREA (singleton tab) with `id` selected inside it.
  const openQueryTab = (id: string) => {
    setSelectedQueryId(id);
    setOpenTabs((tabs) => openTab(tabs, { kind: "query" }));
    setActiveKey("query");
  };
  const closeWorkspaceTab = (key: string) => {
    setActiveKey((ak) => nextActiveKey(openTabs, key, ak));
    setOpenTabs((tabs) => closeTab(tabs, key));
  };
  const addQuery = () => {
    const tbls = tablesQ.data ?? [];
    if (tbls.length === 0) return;
    const id = genId(new Set(ast.queries.map((q) => q.id)));
    commitAst({
      ...ast,
      queries: [
        ...ast.queries,
        { id, name: `query${ast.queries.length + 1}`, source_kind: "table", table_id: tbls[0].id },
      ],
    });
    openQueryTab(id);
  };
  const patchQuery = (id: string, next: Partial<PageQuery>) =>
    commitAst({
      ...ast,
      queries: ast.queries.map((q) => (q.id === id ? { ...q, ...next } : q)),
    });
  const removeQuery = (id: string) => {
    const q = ast.queries.find((x) => x.id === id);
    // Delete-guard: warn (with the referencing components) before dropping a
    // query something still reads — incl. the opaque custom_component / js case.
    const consumers = q ? consumerLabels(pageRefs?.query[q.name]) : [];
    if (q && (consumers.length > 0 || pageRefs?.hasOpaque)) {
      const lead = consumers.length
        ? `La query "${q.name}" è usata da: ${consumers.join(", ")}.`
        : `La query "${q.name}" non risulta usata staticamente, ma la pagina ha un componente custom o una query JS che potrebbe leggerla.`;
      if (!window.confirm(`${lead}\nEliminarla comunque?`)) return;
    }
    commitAst({ ...ast, queries: ast.queries.filter((x) => x.id !== id) });
    // The Query AREA stays open (it picks the next query); just drop the selection.
    setSelectedQueryId((cur) => (cur === id ? null : cur));
  };
  // ── Console run logs (WS-3b) ─────────────────────────────────────────────
  const pushLog = (label: string, lines: string[], level: "info" | "error") =>
    setLogs((prev) =>
      [...prev, { id: `log${logSeq.current++}`, time: nowHms(), label, lines, level }].slice(-200),
    );
  const clearLogs = () => setLogs([]);
  // WS-5 — record a run's outcome into the Console Response tab + focus it (the
  // dock auto-opens). `responseNonce` bumps so the Console switches to Response.
  const recordResponse = (r: ConsoleResponse) => {
    setLastResponse(r);
    setResponseNonce((n) => n + 1);
    setDataDockOpen(true);
  };
  // A previewExpr that CAPTURES console output (for the JS Object tester's explicit
  // runs) and records a log entry — the plain `previewExpr` (used by the inspector
  // for continuous binding eval) stays unlogged so the log isn't spammed.
  const loggingPreviewExpr = (tpl: string) => {
    const { result, lines } = captureConsole(() => previewExpr(tpl));
    const failed = result !== null && result.ok === false;
    const label = tpl.replace(/^\s*\{\{\s*/, "").replace(/\s*\}\}\s*$/, "");
    pushLog(label, [...lines, ...(failed ? [result.error] : [])], failed ? "error" : "info");
    // Mirror the explicit JS-tester run into the Response tab (Appsmith).
    if (result !== null) {
      recordResponse(
        result.ok
          ? { kind: "js", label, time: nowHms(), ok: true, display: result.display }
          : { kind: "js", label, time: nowHms(), ok: false, error: result.error },
      );
    }
    return result;
  };
  // Server-side preview for an http/js query (run on an explicit click): render
  // the {{ }} params against the preview scope + post concrete values (the
  // server SSRF-guards + origin-locks the url). The run is logged + shown in
  // the Console Response tab.
  const runQueryPreview = async (q: PageQuery) => {
    const params = computeRuntimeParams(q, exprScope);
    try {
      const res = await appsApi.previewResource(appId, { spec: q, ...params });
      pushLog(`Query: ${q.name}`, [`${res.total} righe`], "info");
      recordResponse({
        kind: "query",
        label: q.name,
        time: nowHms(),
        ok: true,
        rows: res.rows,
        total: res.total,
      });
      return { rows: res.rows, total: res.total };
    } catch (e) {
      const error = e instanceof Error ? e.message : String(e);
      pushLog(`Query: ${q.name}`, [error], "error");
      recordResponse({ kind: "query", label: q.name, time: nowHms(), ok: false, error });
      throw e;
    }
  };
  // The query OPEN inside the Query area — the selection, falling back to the
  // first query so the area is never blank when queries exist.
  const activeQuery =
    ast.queries.find((q) => q.id === selectedQueryId) ?? ast.queries[0] ?? null;

  // ── JS Objects (WS-2) — keyed by NAME (no id; the language addresses them by
  // name). Rename remaps the open tab so the editor stays put. ──────────────
  const jsObjectsList = ast.js_objects ?? [];
  // Open the JS AREA (singleton tab) with `name` selected inside it.
  const openJsTab = (name: string) => {
    setSelectedJsName(name);
    setOpenTabs((tabs) => openTab(tabs, { kind: "js" }));
    setActiveKey("js");
  };
  const addJsObject = () => {
    const used = new Set(jsObjectsList.map((o) => o.name));
    let i = jsObjectsList.length + 1;
    let name = `JS${i}`;
    while (used.has(name)) name = `JS${++i}`;
    commitAst({ ...ast, js_objects: [...jsObjectsList, { name, code: JS_OBJECT_STARTER }] });
    openJsTab(name);
  };
  const patchJsObject = (oldName: string, next: { name: string; code: string }) => {
    // A rename must CASCADE to the bindings (`{{ Old.fn() }}`) + `run_js` actions
    // that reference the namespace — else they silently break (the same class as
    // `renameNodeId` for modal targets). No-op when the name is unchanged.
    const nextRoot =
      next.name !== oldName ? renameJsObjectRefs(ast.root, oldName, next.name) : ast.root;
    commitAst({
      ...ast,
      root: nextRoot,
      js_objects: jsObjectsList.map((o) => (o.name === oldName ? next : o)),
    });
    // A rename moves the SELECTION (the JS area is a singleton tab now), so the
    // editor stays on the renamed object.
    if (next.name !== oldName) {
      setSelectedJsName((cur) => (cur === oldName ? next.name : cur));
    }
  };
  const removeJsObject = (name: string) => {
    commitAst({ ...ast, js_objects: jsObjectsList.filter((o) => o.name !== name) });
    setSelectedJsName((cur) => (cur === name ? null : cur));
  };
  const activeJs =
    jsObjectsList.find((o) => o.name === selectedJsName) ?? jsObjectsList[0] ?? null;

  // WS-8 (owner UX pass) — EVERY area is now master/detail like Query: the LIST
  // lives in the lateral sidebar and the DETAIL in the content. So all areas
  // except the single-pane Settings carry a left list panel; only `ui` carries
  // the right inspector. The tab bar moved ABOVE this grid (to the right of the
  // rail), so the rail is its own full-height column outside the grid.
  const AREAS_WITH_LIST = new Set([
    "ui",
    "query",
    "js",
    "state",
    "globalState",
    "jsLibraries",
    "customComponents",
    "appQueries",
    "appRest",
    "appRestDocs",
    "appGraphql",
    "appTables",
    "appDatasets",
    "appLinkedDatasets",
    "appLinkedKbs",
    "appLinkedReports",
    "appForms",
  ]);
  const showLeftPanel = AREAS_WITH_LIST.has(activeKey);
  const showInspector = activeKey === "ui";
  // ADR-0048 Slice 2 — INTERFACCE = a list of interfaces (shells over the app).
  // The active interface is DERIVED from the active area (single source of
  // truth): the assistant + form areas ARE interfaces; everything else lives
  // under the "web app" interface (its pages + per-page contexts + the shared
  // frontend resources). The header interface-selector + the per-page rail zone
  // + the page selector are gated on this.
  // ADR-0049 Slice B — Chat & Voce are SEPARATE tabs (independent brains), so the
  // active interface is read straight off the active tab key — no second state.
  const activeInterface: "web" | "chat" | "voice" | "form" | "rest" | "graphql" =
    activeKey === "appInterfaceChat"
      ? "chat"
      : activeKey === "appInterfaceVoice"
        ? "voice"
        : activeKey === "appRest" || activeKey === "appRestDocs"
          ? "rest"
          : activeKey === "appGraphql" || activeKey === "appGraphqlDocs"
            ? "graphql"
            : activeKey === "appForms"
              ? "form"
              : "web";
  // The INNER workspace grid (sibling of the tab bar, right of the rail). Left =
  // 264px when the area has a list, else collapsed; the right inspector column
  // exists ONLY in the UI editor.
  const gridTemplateColumns = `${showLeftPanel ? "264px " : ""}minmax(0,1fr)${
    showInspector ? " 320px" : ""
  }`;

  // Stato (WS-2) is a singleton tab — its editor (StateEditor) opens in the main.
  const openStateTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "state" }));
    setActiveKey("state");
  };
  // WS-4b — the two APP-GLOBAL contexts (rail bottom zone). The $global state
  // hooks live up top (with the other hooks); these are just plain handlers.
  const onGlobalStateChange = (gs: AppGlobalStateEntry[]) => {
    setGlobalState(gs);
    if (gsSaveTimer.current) clearTimeout(gsSaveTimer.current);
    gsSaveTimer.current = setTimeout(() => saveGlobalState.mutate(gs), 600);
  };
  const openGlobalStateTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "globalState" }));
    setActiveKey("globalState");
  };
  const openSettingsTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "settings" }));
    setActiveKey("settings");
  };
  // ADR-0046 — the two app-global library managers (rail bottom zone).
  const openJsLibrariesTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "jsLibraries" }));
    setActiveKey("jsLibraries");
  };
  const openCustomComponentsTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "customComponents" }));
    setActiveKey("customComponents");
  };
  // ADR-0047 — the app-level Query manager (rail bottom zone).
  const openAppQueriesTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appQueries" }));
    setActiveKey("appQueries");
  };
  // ADR-0048 single-IDE — the app data-table manager (rail bottom zone).
  const openAppTablesTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appTables" }));
    setActiveKey("appTables");
  };
  // ADR-0048 single-IDE — the app datasets (projections) manager (rail bottom).
  const openAppDatasetsTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appDatasets" }));
    setActiveKey("appDatasets");
  };
  const openAppFormsTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appForms" }));
    setActiveKey("appForms");
  };
  // ADR-0059 — the two Core areas for external resources the app CONSUMES.
  const openAppLinkedDatasetsTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appLinkedDatasets" }));
    setActiveKey("appLinkedDatasets");
  };
  const openAppLinkedKbsTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appLinkedKbs" }));
    setActiveKey("appLinkedKbs");
  };
  // ADR-0060 S5 — the Core area for Report Types the app consumes.
  const openAppLinkedReportsTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appLinkedReports" }));
    setActiveKey("appLinkedReports");
  };
  // Switch the whole IDE context. Leaving a context whose active area doesn't
  // belong to the destination, land on that context's default area so the rail
  // and the main pane stay in sync.
  const switchContext = (ctx: "interfacce" | "core" | "mappa") => {
    if (ctx === builderContext) return;
    setBuilderContext(ctx);
    // "mappa" is a full-canvas overlay (no rail/tab) — it doesn't touch the
    // active area, so returning to core/interfacce lands where you left.
    if (ctx === "core") {
      if (!CORE_AREA_KEYS.has(activeKey)) openAppTablesTab();
    } else if (ctx === "interfacce" && CORE_AREA_KEYS.has(activeKey)) {
      setActiveKey("ui");
    }
  };
  // The Mappa jumps INTO the right editor when a node is clicked (the map is the
  // navigation hub tying Core ⇄ Interfacce). Each opener flips context first.
  const openTableInCore = (tableId: string) => {
    switchContext("core");
    openAppTablesTab();
    setSelectedAppTableId(tableId);
  };
  const openDatasetInCore = (datasetId: string) => {
    switchContext("core");
    openAppDatasetsTab();
    setSelectedAppDatasetId(datasetId);
  };
  const openQueryInCore = (queryId: string) => {
    switchContext("core");
    openAppQueriesTab();
    setSelectedAppQueryId(queryId);
  };
  const openPageInInterfacce = (pageId: string) => {
    switchContext("interfacce");
    setActiveKey("ui");
    navigate(`/apps/${appId}/pages/${pageId}`);
  };
  const openFormsInInterfacce = () => {
    switchContext("interfacce");
    openAppFormsTab();
  };
  // ADR-0048 single-IDE — Members & roles + Interfaces (single-pane, rail bottom).
  const openAppMembersTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appMembers" }));
    setActiveKey("appMembers");
  };
  // ADR-0049 Slice B — Chat & Voce open as their OWN tabs (each a flat front door).
  const openAppInterfaceTab = (kind: "chat" | "voice") => {
    const tabKind = kind === "voice" ? "appInterfaceVoice" : "appInterfaceChat";
    setOpenTabs((tabs) => openTab(tabs, { kind: tabKind }));
    setActiveKey(tabKind);
  };
  // ADR-0051 — REST (the single exposure registry): two CORE rail areas — the
  // endpoints editor (master/detail) + the OpenAPI documentation.
  const openAppRestTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appRest" }));
    setActiveKey("appRest");
  };
  const openAppRestDocsTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appRestDocs" }));
    setActiveKey("appRestDocs");
  };
  // ADR-0058 — GraphQL (the SDL-derived twin of REST): the resources editor + the
  // GraphiQL playground (test + live introspection docs).
  const openAppGraphqlTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appGraphql" }));
    setActiveKey("appGraphql");
  };
  const openAppGraphqlDocsTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appGraphqlDocs" }));
    setActiveKey("appGraphqlDocs");
  };
  const openAppScheduledRulesTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appScheduledRules" }));
    setActiveKey("appScheduledRules");
  };
  const openAppAutomationsTab = () => {
    setOpenTabs((tabs) => openTab(tabs, { kind: "appAutomations" }));
    setActiveKey("appAutomations");
  };
  // Tab-bar views (label + icon kind) resolved from the current page's entities.
  const tabViews: WorkspaceTabView[] = openTabs.map((t) => {
    if (t.kind === "ui") return { key: "ui", kind: "ui", label: `UI: ${settings.title}`, closable: false };
    if (t.kind === "state") return { key: "state", kind: "state", label: "Stato", closable: true };
    // WS-7 — `query` / `js` are singleton master/detail AREAS (one tab each;
    // the selected entity lives in component state, shown by the area's list).
    if (t.kind === "query") return { key: "query", kind: "query", label: "Query", closable: true };
    if (t.kind === "js") return { key: "js", kind: "js", label: "JS", closable: true };
    // WS-4b — app-global contexts (rail bottom).
    if (t.kind === "globalState")
      return { key: "globalState", kind: "globalState", label: "Stato globale", closable: true };
    // ADR-0046 — app-global libraries (rail bottom).
    if (t.kind === "jsLibraries")
      return { key: "jsLibraries", kind: "jsLibraries", label: "Librerie JS", closable: true };
    if (t.kind === "customComponents")
      return { key: "customComponents", kind: "customComponents", label: "Libreria componenti", closable: true };
    // ADR-0047 — app-level Query manager (rail bottom).
    if (t.kind === "appQueries")
      return { key: "appQueries", kind: "appQueries", label: "Query app", closable: true };
    // ADR-0048 single-IDE — app data-table manager (rail bottom).
    if (t.kind === "appTables")
      return { key: "appTables", kind: "appTables", label: "Tabelle app", closable: true };
    // ADR-0048 single-IDE — app datasets (projections) manager (rail bottom).
    if (t.kind === "appDatasets")
      return { key: "appDatasets", kind: "appDatasets", label: "Proiezioni", closable: true };
    if (t.kind === "appForms")
      return { key: "appForms", kind: "appForms", label: "Form", closable: true };
    if (t.kind === "appLinkedDatasets")
      return { key: "appLinkedDatasets", kind: "appLinkedDatasets", label: "Dataset", closable: true };
    if (t.kind === "appLinkedKbs")
      return { key: "appLinkedKbs", kind: "appLinkedKbs", label: "Knowledge base", closable: true };
    if (t.kind === "appLinkedReports")
      return { key: "appLinkedReports", kind: "appLinkedReports", label: "Report", closable: true };
    // ADR-0048 single-IDE — Members & roles + Interfaces (single-pane, rail bottom).
    if (t.kind === "appMembers")
      return { key: "appMembers", kind: "appMembers", label: "Membri e ruoli", closable: true };
    if (t.kind === "appInterfaceChat")
      return { key: "appInterfaceChat", kind: "appInterfaceChat", label: "Chat", closable: true };
    if (t.kind === "appInterfaceVoice")
      return { key: "appInterfaceVoice", kind: "appInterfaceVoice", label: "Voce", closable: true };
    if (t.kind === "appRest")
      return { key: "appRest", kind: "appRest", label: "REST", closable: true };
    if (t.kind === "appRestDocs")
      return { key: "appRestDocs", kind: "appRestDocs", label: "Docs API", closable: true };
    if (t.kind === "appGraphql")
      return { key: "appGraphql", kind: "appGraphql", label: "GraphQL", closable: true };
    if (t.kind === "appGraphqlDocs")
      return { key: "appGraphqlDocs", kind: "appGraphqlDocs", label: "GraphiQL", closable: true };
    if (t.kind === "appScheduledRules")
      return { key: "appScheduledRules", kind: "appScheduledRules", label: "Regole pianificate", closable: true };
    if (t.kind === "appAutomations")
      return { key: "appAutomations", kind: "appAutomations", label: "Automazioni", closable: true };
    return { key: "settings", kind: "settings", label: "Impostazioni", closable: true };
  });
  // JS Object handler refs `<Name>.<method>` (ADR-0044 J2) for the `run_js`
  // action picker — read off the compiled (cached) namespaces.
  const jsFunctions: string[] = (() => {
    const { namespaces } = compileJsObjectsCached(ast.js_objects, appLibraries);
    const out: string[] = [];
    for (const [name, ns] of Object.entries(namespaces)) {
      for (const fn of Object.keys(ns)) {
        if (typeof (ns as Record<string, unknown>)[fn] === "function") out.push(`${name}.${fn}`);
      }
    }
    return out;
  })();
  // Binding options for the visibility ConditionBuilder's field dropdown —
  // mirrors the Data panel surface so authors PICK a real binding instead of
  // typing it: user/role, page vars + state keys, each query's total + columns,
  // and repeater `$item` columns.
  const conditionFields: string[] = (() => {
    const out = ["$user.email", "$user.full_name", "$role.slug", "$role.name"];
    for (const k of Object.keys((exprScope.$page as Record<string, unknown>) ?? {})) {
      out.push(`$page.${k}`);
    }
    for (const q of ast.queries) {
      out.push(`$queries.${q.name}.total`);
      const first = previewQ.data?.[q.name]?.rows?.[0];
      if (first) for (const c of Object.keys(first)) out.push(`$queries.${q.name}.rows[0].${c}`);
    }
    for (const c of repeaterColumns) out.push(`$item.${c}`);
    return [...new Set(out)];
  })();
  // Binding → value type, to pre-fill the condition builder's type chip (A1).
  // Prefer the REAL column schema (`columnValueType` from the table's data_type)
  // — correct even with no preview rows — and fall back to inferring from the
  // sample value for things without a schema ($page vars, computed/aggregate).
  const columnTypes = columnsByTableQ.data?.types ?? {};
  const conditionFieldTypes: Record<string, ValueType> = (() => {
    const out: Record<string, ValueType> = {};
    const page = (exprScope.$page as Record<string, unknown>) ?? {};
    for (const [k, v] of Object.entries(page)) out[`$page.${k}`] = inferValueType(v);
    for (const q of ast.queries) {
      out[`$queries.${q.name}.total`] = "number";
      const schema = columnTypes[q.table_id ?? ""] ?? {};
      const first = previewQ.data?.[q.name]?.rows?.[0];
      if (first)
        for (const [c, v] of Object.entries(first))
          out[`$queries.${q.name}.rows[0].${c}`] = schema[c] ?? inferValueType(v);
    }
    const itemSchema = repeater.tableId ? (columnTypes[repeater.tableId] ?? {}) : {};
    const itemSample = (repeater.sample ?? {}) as Record<string, unknown>;
    for (const c of repeaterColumns)
      out[`$item.${c}`] = itemSchema[c] ?? inferValueType(itemSample[c]);
    return out;
  })();

  return (
    <div className="flex h-full flex-col">
      {/* Top bar: settings + save */}
      <div className="flex items-center gap-2 border-b border-border bg-surface px-3 py-1.5">
        <button
          className="flex items-center gap-1 rounded px-1.5 py-1 text-sm text-muted-foreground hover:text-foreground"
          title="Torna all'elenco app"
          onClick={() => navigate("/apps")}
        >
          {/* Post-cutover (ADR-0048): `/apps/:appId` redirects back INTO the
              builder, so the back button must go to the apps LIST, not the app. */}
          <ArrowLeft className="h-4 w-4" /> App
        </button>
        {/* ADR-0048 — DECLARED context switch: the whole IDE flips between the
            INTERFACCE (frontend shells over the app) and CORE (backend: data ·
            logic · access) workspaces. The page selector below belongs only to
            the web-app interface → shown only in INTERFACCE. */}
        <div className="ml-1 flex items-center rounded-md border border-border bg-background p-0.5 text-xs font-medium">
          {([
            ["interfacce", LayoutGrid, "Interfacce"],
            ["core", Database, "Core"],
            ["mappa", Waypoints, "Mappa"],
          ] as const).map(([ctx, Icon, label]) => (
            <button
              key={ctx}
              type="button"
              onClick={() => switchContext(ctx)}
              aria-pressed={builderContext === ctx}
              className={`flex items-center gap-1 rounded px-2 py-0.5 transition-colors ${
                builderContext === ctx
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {label}
            </button>
          ))}
        </div>
        {builderContext === "interfacce" && (
          <>
            <span className="text-muted-foreground/40">/</span>
        {/* ADR-0048 Slice 2 — INTERFACE selector: the primary nav of the
            INTERFACCE context. Each entry is an interface (a shell over the
            app's backend): the web app (pages), the assistant (chat/voce), the
            forms. Future kinds (widget, bot, API) slot in here. The web-app
            page selector + per-page rail nest UNDER the "Web app" interface. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 rounded px-2 py-1 text-sm font-medium text-foreground hover:bg-elevated">
              {(() => {
                const Icon =
                  activeInterface === "chat"
                    ? MessageSquare
                    : activeInterface === "voice"
                      ? Mic
                      : activeInterface === "rest"
                        ? Webhook
                        : activeInterface === "form"
                          ? FileText
                          : Globe;
                const label =
                  activeInterface === "chat"
                    ? "Chat"
                    : activeInterface === "voice"
                      ? "Voce"
                      : activeInterface === "rest"
                        ? "REST"
                        : activeInterface === "form"
                          ? "Form"
                          : "Web app";
                return (
                  <>
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="max-w-[160px] truncate">{label}</span>
                  </>
                );
              })()}
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            {/* ADR-0049 — chat ≠ voce (independent brains); grouped by domain. */}
            {([
              ["UI", [["web", Globe, "Web app", "Applicazione web"]]],
              ["Assistenti AI", [
                ["chat", MessageSquare, "Chat", "Assistente chat"],
                ["voice", Mic, "Voce", "Assistente vocale"],
              ]],
              ["Web services", [
                ["rest", Webhook, "REST", "Api REST"],
                ["graphql", Network, "GraphQL", "Api GraphQL"],
              ]],
              ["Miscellanea", [["form", FileText, "Form", "Modulo di raccolta dati"]]],
            ] as const).map(([group, items], gi) => (
              <div key={group} className={gi > 0 ? "mt-1 border-t border-border/50 pt-1" : ""}>
                <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {group}
                </div>
                {items.map(([k, Icon, label, hint]) => (
                  <DropdownMenuItem
                    key={k}
                    onSelect={() => {
                      if (k === "web") setActiveKey("ui");
                      else if (k === "chat") openAppInterfaceTab("chat");
                      else if (k === "voice") openAppInterfaceTab("voice");
                      else if (k === "rest") openAppRestTab();
                      else if (k === "graphql") openAppGraphqlTab();
                      else openAppFormsTab();
                    }}
                    // The shared item is a fixed h-7 single-liner; these are
                    // two-line (label + hint), so override to auto-height + top-align.
                    className={`h-auto items-start gap-2 py-1.5 ${activeInterface === k ? "font-medium text-foreground" : ""}`}
                  >
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{label}</span>
                      <span className="truncate text-[11px] text-muted-foreground">{hint}</span>
                    </span>
                  </DropdownMenuItem>
                ))}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {activeInterface === "web" && (
          <>
            <span className="text-muted-foreground/40">/</span>
        {/* WS-6 — the page selector + per-page actions live in the breadcrumb
            dropdown (replaces the inline meta strip + the rail "Pagine" icon);
            the page metadata moved to "Impostazioni pagina" (a dialog). Nested
            under the Web-app interface — the pages ARE the web app. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 rounded px-2 py-1 text-sm font-medium text-foreground hover:bg-elevated">
              {settings.overlay && (
                <span className="rounded bg-accent/15 px-1 text-[10px] font-semibold uppercase tracking-wide text-accent">
                  {settings.overlay_mode}
                </span>
              )}
              <span className="max-w-[200px] truncate">{settings.title || "Pagina"}</span>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              Pagine
            </div>
            <div className="max-h-72 overflow-y-auto">
              {(pagesQ.data ?? []).map((p) => (
                <DropdownMenuItem
                  key={p.id}
                  onSelect={() => {
                    if (p.id !== pageId) navigate(`/apps/${appId}/pages/${p.id}`);
                  }}
                  className={p.id === pageId ? "font-medium text-foreground" : ""}
                >
                  <span className="truncate">{p.title}</span>
                </DropdownMenuItem>
              ))}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setCreateOpen(true)} className="gap-2 text-accent">
              <Plus className="h-3.5 w-3.5" /> Nuova pagina
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setPageSettingsOpen(true)} className="gap-2">
              <Settings className="h-3.5 w-3.5" /> Impostazioni pagina
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
          </>
        )}
          </>
        )}
        <div className="ml-auto flex items-center gap-3">
          {error && <span className="text-sm text-danger">{error}</span>}
          {dirty ? (
            <span className="flex items-center gap-1.5 text-sm text-warning">
              <span className="h-1.5 w-1.5 rounded-full bg-warning" />
              Non salvato
            </span>
          ) : (
            save.isSuccess && !save.isPending && <span className="text-sm text-success">Salvato</span>
          )}
          <Button onClick={() => save.mutate()} disabled={save.isPending || !dirty}>
            {save.isPending ? "Salvataggio…" : "Salva"}
          </Button>
        </div>
      </div>

      {/* MAPPA — the app-structure map (3rd context). A full-canvas overlay: it
          replaces the rail+grid body (which stays mounted-but-hidden so its
          caches stay warm). Clicking a node jumps into the right editor. */}
      {builderContext === "mappa" && (
        <div className="min-h-0 flex-1 overflow-hidden">
          <AppStructureMap
            appId={appId}
            canWrite
            onOpenTable={openTableInCore}
            onOpenDataset={openDatasetInCore}
            onOpenQuery={openQueryInCore}
            onOpenPage={openPageInInterfacce}
            onOpenForm={openFormsInInterfacce}
          />
        </div>
      )}
      {/* WS-8 — outer FLEX ROW: the icon rail is its OWN full-height column; to
          its RIGHT a STACK holds the tab bar ON TOP and the
          [list | content | inspector] grid below — so the tabs sit above
          sidebar + content (not above the rail). */}
      <div className={`${builderContext === "mappa" ? "hidden" : "flex"} flex-1 overflow-hidden`}>
        <div className="flex w-11 shrink-0 flex-col gap-1 border-r border-border bg-surface/40 p-1.5">
            {(() => {
              // WS-4b — the rail has TWO zones, position = scope: the TOP group is
              // PER-PAGE (resets on page switch); the BOTTOM-anchored group is
              // APP-GLOBAL. `state`/`globalState`/`settings` are singleton workspace
              // tabs (open in the main pane); the rest swap the rail side-panel.
              type RailEntry = readonly [string, typeof Variable, string, string];
              // WS-7 — every rail icon is a top-level AREA (a singleton tab). The
              // rail is a PURE switcher: clicking it opens that area's tab and
              // makes it active. `ui`/`query`/`js` are master/detail (the area
              // carries its own left list); the rest are full-width singletons.
              // Insert+Layers merged under the single `ui` icon; Pages moved to
              // the breadcrumb dropdown.
              const TOP: RailEntry[] = [
                ["ui", LayoutGrid, "UI", "Editor visuale + Layer della pagina"],
                ["query", Database, "Query", "Query della pagina"],
                ["js", Braces, "JS", "JS Objects della pagina"],
                ["state", Variable, "Stato pagina", "Stato pagina ($page) — si azzera cambiando pagina"],
              ];
              // ADR-0048 Slice 2 — INTERFACCE context, web-app frontend
              // resources (bottom zone): the shared building blocks of the
              // shells + portal settings. The assistant + form INTERFACES moved
              // OUT of the rail into the header interface-selector (they're
              // peers of the web app, not areas under it).
              const INTERFACCE_BOTTOM: RailEntry[] = [
                ["globalState", Globe, "Stato globale", "Stato globale ($global) — sopravvive alla navigazione"],
                ["customComponents", Boxes, "Libreria componenti", "Componenti custom riutilizzabili (HTML/CSS/JS o React)"],
                ["jsLibraries", Package, "Librerie JS", "Librerie JS esterne (lodash, dayjs, …) usabili nei binding"],
                ["settings", Settings, "Impostazioni", "Impostazioni del portale (branding, sicurezza, navigazione)"],
              ];
              // ADR-0051 — the REST INTERFACE's own two areas (top zone when active):
              // the endpoints editor + the OpenAPI documentation.
              const REST_RAIL: RailEntry[] = [
                ["appRest", Webhook, "REST", "Endpoint API REST su tabelle, proiezioni e Query (registro unico)"],
                ["appRestDocs", BookText, "Docs API", "Documentazione OpenAPI live degli endpoint REST"],
              ];
              // ADR-0058 — the GraphQL INTERFACE's own area (single endpoint,
              // SDL derived from the exposed resources).
              const GRAPHQL_RAIL: RailEntry[] = [
                ["appGraphql", Network, "GraphQL", "Risorse esposte su un unico endpoint GraphQL (schema derivato)"],
                ["appGraphqlDocs", FlaskConical, "GraphiQL", "Prova + documentazione: editor query, esecuzione, esploratore schema (introspection)"],
              ];
              // ADR-0048 — CORE context (data · logic · access). No per-page
              // canvas here: it's the app's backend, not a page.
              const CORE: RailEntry[] = [
                ["appTables", Table2, "Tabelle", "Tabelle dati dell'app (colonne, relazioni, vincoli)"],
                ["appDatasets", Layers, "Proiezioni", "Proiezioni read-model (join, colonne, filtri) sulle tabelle"],
                ["appLinkedDatasets", Database, "Dataset", "Dataset del catalogo collegati all'app (visibili ai ruoli in «Membri e ruoli»)"],
                ["appLinkedKbs", BookOpen, "Knowledge base", "Knowledge base collegate all'app (visibili ai ruoli in «Membri e ruoli»)"],
                ["appLinkedReports", FileBarChart2, "Report", "Tipi di report collegati all'app (renderizzabili dai ruoli in «Membri e ruoli»)"],
                ["appQueries", Server, "Query app", "Logiche di backend (Query/Logic) — invocabili, pianificate, REST"],
                ["appScheduledRules", AlarmClock, "Regole pianificate", "Regole pianificate (cron) sui dati dell'app"],
                ["appAutomations", Zap, "Automazioni", "Workflow dell'app (reagiscono a row_changed / form_submitted)"],
                ["appMembers", Users, "Membri e ruoli", "Membri, ruoli, campi profilo e matrice di accesso dell'app"],
              ];
              const onIcon = (key: string) => {
                // Every icon opens its AREA tab + makes it active (pure switcher).
                if (key === "ui") setActiveKey("ui");
                else if (key === "query") {
                  setOpenTabs((tabs) => openTab(tabs, { kind: "query" }));
                  setActiveKey("query");
                } else if (key === "js") {
                  setOpenTabs((tabs) => openTab(tabs, { kind: "js" }));
                  setActiveKey("js");
                } else if (key === "state") openStateTab();
                else if (key === "globalState") openGlobalStateTab();
                else if (key === "jsLibraries") openJsLibrariesTab();
                else if (key === "customComponents") openCustomComponentsTab();
                else if (key === "appQueries") openAppQueriesTab();
                else if (key === "appRest") openAppRestTab();
                else if (key === "appRestDocs") openAppRestDocsTab();
                else if (key === "appGraphql") openAppGraphqlTab();
                else if (key === "appGraphqlDocs") openAppGraphqlDocsTab();
                else if (key === "appTables") openAppTablesTab();
                else if (key === "appDatasets") openAppDatasetsTab();
                else if (key === "appLinkedDatasets") openAppLinkedDatasetsTab();
                else if (key === "appLinkedKbs") openAppLinkedKbsTab();
                else if (key === "appLinkedReports") openAppLinkedReportsTab();
                else if (key === "appForms") openAppFormsTab();
                else if (key === "appMembers") openAppMembersTab();
                else if (key === "appInterfaceChat") openAppInterfaceTab("chat");
                else if (key === "appInterfaceVoice") openAppInterfaceTab("voice");
                else if (key === "appScheduledRules") openAppScheduledRulesTab();
                else if (key === "appAutomations") openAppAutomationsTab();
                else if (key === "settings") openSettingsTab();
              };
              const renderIcon = ([key, Icon, label, hint]: RailEntry) => {
                // The rail mirrors the active AREA exactly — one icon lit at a time.
                const pressed = activeKey === key;
                return (
                  <Tooltip
                    key={key}
                    side="right"
                    content={
                      <div className="flex flex-col">
                        <span className="font-medium">{label}</span>
                        <span className="text-muted-foreground">{hint}</span>
                      </div>
                    }
                  >
                    <button
                      onClick={() => onIcon(key)}
                      aria-label={label}
                      aria-pressed={pressed}
                      className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                        pressed
                          ? "bg-accent text-accent-foreground"
                          : "text-muted-foreground hover:bg-surface hover:text-foreground"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  </Tooltip>
                );
              };
              // ADR-0048 — the rail renders ONLY the active context. CORE: the
              // backend areas (no per-page zone — there's no canvas). INTERFACCE:
              // the per-page contexts (top) belong to the WEB-APP interface, so
              // they show only when it's active (Slice 2); the app-frontend
              // resources (bottom) stay reachable across interfaces.
              if (builderContext === "core") {
                return <>{CORE.map(renderIcon)}</>;
              }
              return (
                <>
                  {activeInterface === "web" && TOP.map(renderIcon)}
                  {activeInterface === "rest" && REST_RAIL.map(renderIcon)}
                  {activeInterface === "graphql" && GRAPHQL_RAIL.map(renderIcon)}
                  {/* Stato globale · Componenti · Librerie JS · Impostazioni are WEB-app
                      frontend resources — hidden on chat/voce/rest/form. */}
                  {activeInterface === "web" && (
                    <div className="mt-auto flex flex-col gap-1 border-t border-border/60 pt-1.5">
                      {INTERFACCE_BOTTOM.map(renderIcon)}
                    </div>
                  )}
                </>
              );
            })()}
        </div>

        {/* WS-8 — tab bar ABOVE the [list | content | inspector] grid, to the
            RIGHT of the rail. */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <WorkspaceTabs
            views={tabViews}
            activeKey={activeKey}
            onActivate={setActiveKey}
            onClose={closeWorkspaceTab}
          />
          <div className="grid flex-1 overflow-hidden" style={{ gridTemplateColumns }}>
            {showLeftPanel && (
            <aside className="overflow-y-auto border-r border-border p-3">
            {/* The UI area carries its own Insert|Layers toggle below; the
                list panels (Query/JS) get a simple heading. */}
            {(activeKey === "query" || activeKey === "js") && (
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {activeKey === "query" ? "Query" : "JS"}
              </h3>
            )}

            {activeKey === "ui" && (
              <div>
                {/* WS-6 — Insert (component palette) vs Layers (outline), merged
                    here from the old separate rail icons. */}
                <div className="mb-3 flex items-center rounded-md border border-border/60 p-0.5 text-xs">
                  {(
                    [
                      ["insert", "Inserisci"],
                      ["layers", "Layers"],
                    ] as const
                  ).map(([k, lbl]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setUiPanel(k)}
                      className={`flex-1 rounded px-2 py-1 font-medium ${
                        uiPanel === k
                          ? "bg-accent/15 text-accent"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
                {uiPanel === "insert" && (
                <div>
                <div className="relative mb-3">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={paletteQuery}
                    onChange={(e) => setPaletteQuery(e.target.value)}
                    placeholder="Search components…"
                    className="w-full rounded-md border border-border bg-surface/40 py-1.5 pl-7 pr-2 text-xs outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
                  />
                </div>
                {(() => {
                  const q = paletteQuery.trim().toLowerCase();
                  const match = (c: (typeof catalog)[number]) =>
                    !q ||
                    c.label.toLowerCase().includes(q) ||
                    c.type.toLowerCase().includes(q) ||
                    (c.description ?? "").toLowerCase().includes(q);
                  const cats = ["layout", "display", "data", "input", "action"];
                  const anyMatch = catalog.some(match);
                  if (!anyMatch)
                    return (
                      <p className="px-1 text-xs text-muted-foreground">
                        No components match “{paletteQuery}”.
                      </p>
                    );
                  return cats.map((cat) => {
                  const items = catalog.filter((c) => c.category === cat && match(c));
                  if (items.length === 0) return null;
                  return (
                    <div key={cat} className="mb-3">
                      <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
                        {cat}
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {items.map((c) => {
                          const Icon = paletteIcon(c.type);
                          return (
                            <button
                              key={c.type}
                              // Click = add under the selection; drag = drop at
                              // a precise spot on the canvas (`new:<type>`).
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData("text/plain", `new:${c.type}`);
                                // Must match the canvas drop target's dropEffect
                                // ("move"), else the browser rejects the drop.
                                e.dataTransfer.effectAllowed = "move";
                              }}
                              className="flex cursor-grab flex-col items-center gap-1 rounded-md border border-border bg-surface/40 px-2 py-2.5 text-center text-[11px] text-muted-foreground transition-colors hover:border-accent/60 hover:bg-surface hover:text-foreground active:cursor-grabbing"
                              onClick={() => addComponent(c)}
                              title={c.description}
                            >
                              <Icon className="h-4 w-4" />
                              {c.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                  });
                })()}
                <p className="text-[11px] leading-snug text-muted-foreground">
                  Clicca per aggiungere sotto la selezione, o trascina sul canvas.
                </p>
                </div>
                )}
                {uiPanel === "layers" && (
                  <OutlineNode
                    node={ast.root}
                    depth={0}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    onMove={(id, dir) => mutateNode((r) => moveSibling(r, id, dir))}
                    onDrop={dropOnNode}
                    onDelete={(id) => {
                      mutateNode((r) => removeNode(r, id));
                      if (selectedId === id) setSelectedId("root");
                    }}
                    previewBp={previewBreakpoint(viewport, customW)}
                  />
                )}
              </div>
            )}

            {activeKey === "query" && (
              <QueryListPanel
                queries={ast.queries}
                activeId={activeQuery?.id ?? null}
                canAdd={canAddQuery}
                onSelect={openQueryTab}
                onAdd={addQuery}
                references={pageRefs}
                onJump={(u) => {
                  // A query-consumer (its nodeId is a query id) opens that query
                  // tab; any other node is a page component → canvas select.
                  if (ast.queries.some((q) => q.id === u.nodeId)) openQueryTab(u.nodeId);
                  else setSelectedId(u.nodeId);
                }}
              />
            )}

            {activeKey === "js" && (
              <JsObjectListPanel
                jsObjects={jsObjectsList}
                activeName={activeJs?.name ?? null}
                onSelect={openJsTab}
                onAdd={addJsObject}
              />
            )}

            {/* WS-8 — every other area shows its master LIST in the sidebar too
                (the DETAIL is the content), uniform with Query/JS. Selection is
                lifted (here) so the list mount + the detail mount agree. */}
            {activeKey === "state" && (
              <StateEditor
                view="list"
                variables={ast.variables}
                stateKeys={stateKeys}
                references={pageRefs}
                selectedId={selectedVarId}
                onSelectId={setSelectedVarId}
                onChange={(variables) => commitAst({ ...ast, variables })}
                onJump={(u) => {
                  if (ast.queries.some((q) => q.id === u.nodeId)) openQueryTab(u.nodeId);
                  else setSelectedId(u.nodeId);
                }}
              />
            )}
            {activeKey === "globalState" && (
              <GlobalStateEditor
                view="list"
                entries={globalState}
                onChange={onGlobalStateChange}
                saving={saveGlobalState.isPending}
                selectedIdx={selectedGlobalIdx}
                onSelectIdx={setSelectedGlobalIdx}
              />
            )}
            {activeKey === "jsLibraries" && (
              <JsLibrariesPane
                view="list"
                appId={appId}
                selectedName={selectedLibName}
                onSelectName={setSelectedLibName}
              />
            )}
            {activeKey === "customComponents" && (
              <CustomComponentsPane
                view="list"
                appId={appId}
                selectedId={selectedComponentId}
                onSelectId={setSelectedComponentId}
              />
            )}
            {activeKey === "appQueries" && (
              <AppQueriesPane
                view="list"
                appId={appId}
                tables={(tablesQ.data ?? []).map((t) => ({ id: t.id, name: t.name }))}
                datasets={(datasetsQ.data ?? []).map((d) => ({ id: d.id, name: d.name }))}
                columnsByTable={columnsByTableQ.data?.names ?? {}}
                selectedId={selectedAppQueryId}
                onSelectId={setSelectedAppQueryId}
              />
            )}
            {activeKey === "appRest" && (
              <RestInterfaceEditor
                view="list"
                appId={appId}
                canWrite={true}
                selectedId={selectedRestEndpointId}
                onSelectId={setSelectedRestEndpointId}
              />
            )}
            {activeKey === "appRestDocs" && (
              <RestApiDocs
                view="list"
                appId={appId}
                selectedAnchor={selectedRestDocAnchor}
                onSelectAnchor={setSelectedRestDocAnchor}
              />
            )}
            {activeKey === "appGraphql" && (
              <GraphqlInterfaceEditor
                view="list"
                appId={appId}
                canWrite={true}
                selectedId={selectedGraphqlResourceId}
                onSelectId={setSelectedGraphqlResourceId}
              />
            )}
            {activeKey === "appTables" && (
              <AppTablesPane
                view="list"
                appId={appId}
                selectedId={selectedAppTableId}
                onSelectId={setSelectedAppTableId}
              />
            )}
            {activeKey === "appDatasets" && (
              <AppDatasetsPane
                view="list"
                appId={appId}
                selectedId={selectedAppDatasetId}
                onSelectId={setSelectedAppDatasetId}
              />
            )}
            {activeKey === "appLinkedDatasets" && (
              <LinkedResourcesPane
                view="list"
                appId={appId}
                kind="dataset"
                selectedId={selectedLinkedDatasetId}
                onSelectId={setSelectedLinkedDatasetId}
              />
            )}
            {activeKey === "appLinkedKbs" && (
              <LinkedResourcesPane
                view="list"
                appId={appId}
                kind="kb"
                selectedId={selectedLinkedKbId}
                onSelectId={setSelectedLinkedKbId}
              />
            )}
            {activeKey === "appLinkedReports" && (
              <LinkedResourcesPane
                view="list"
                appId={appId}
                kind="report"
                selectedId={selectedLinkedReportId}
                onSelectId={setSelectedLinkedReportId}
              />
            )}
            {activeKey === "appForms" && (
              <AppFormsPane
                view="list"
                appId={appId}
                selectedId={selectedAppFormId}
                onSelectId={setSelectedAppFormId}
              />
            )}

          </aside>
          )}

          {/* Center: the canvas (UI) or the active area's DETAIL. The tab bar
              moved ABOVE this grid (to the right of the rail). */}
          <div className="flex min-w-0 flex-col overflow-hidden">
          {/* WS-6 — UI-mode toolbar: viewport preview + shell + undo/redo. These
              are canvas functions, so they ride the UI tab (out of the global
              header) — irrelevant on Query/JS/Stato tabs. */}
          {activeKey === "ui" && (
            <div className="flex shrink-0 items-center gap-2 border-b border-border bg-surface px-3 py-1">
              <div className="flex items-center rounded-md border border-border p-0.5">
                {(
                  [
                    ["desktop", Monitor, "Desktop"],
                    ["tablet", Tablet, "Tablet (768px)"],
                    ["mobile", Smartphone, "Mobile (400px)"],
                    ["custom", Scaling, "Dimensione personalizzata"],
                  ] as const
                ).map(([vp, Icon, label]) => (
                  <Tooltip key={vp} content={label}>
                    <button
                      onClick={() => setViewport(vp)}
                      aria-label={label}
                      className={`rounded p-1.5 ${
                        viewport === vp
                          ? "bg-accent text-accent-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  </Tooltip>
                ))}
              </div>
              {viewport === "custom" && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <input
                    className="w-16 rounded border border-border bg-background px-1.5 py-1 text-foreground"
                    value={customW}
                    onChange={(e) => setCustomW(e.target.value)}
                    title="Larghezza (valore CSS)"
                    placeholder="100%"
                  />
                  <span>×</span>
                  <input
                    className="w-16 rounded border border-border bg-background px-1.5 py-1 text-foreground"
                    value={customH}
                    onChange={(e) => setCustomH(e.target.value)}
                    title="Altezza (valore CSS, 'auto' = adatta)"
                    placeholder="auto"
                  />
                </div>
              )}
              <Tooltip content="Anteprima dentro lo shell del portale (header / nav / footer)">
                <button
                  onClick={() => setShowShell((s) => !s)}
                  aria-label="Mostra shell del portale"
                  className={`rounded-md border border-border p-1.5 ${
                    showShell ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <AppWindow className="h-4 w-4" />
                </button>
              </Tooltip>
              <div className="ml-auto flex items-center gap-1">
                <Tooltip content="Annulla (⌘Z)">
                  <button
                    className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-surface hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent"
                    onClick={undo}
                    disabled={past.length === 0}
                    aria-label="Annulla"
                  >
                    <Undo2 className="h-4 w-4" />
                  </button>
                </Tooltip>
                <Tooltip content="Ripeti (⌘⇧Z)">
                  <button
                    className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-surface hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent"
                    onClick={redo}
                    disabled={future.length === 0}
                    aria-label="Ripeti"
                  >
                    <Redo2 className="h-4 w-4" />
                  </button>
                </Tooltip>
              </div>
            </div>
          )}
          {activeKey === "query" ? (
            activeQuery ? (
              <div className="min-h-0 flex-1 overflow-hidden bg-background">
                <QueryEditorPane
                  query={activeQuery}
                  tables={(tablesQ.data ?? []).map((t) => ({ id: t.id, name: t.name }))}
                  datasets={(datasetsQ.data ?? []).map((d) => ({ id: d.id, name: d.name }))}
                  columnsByTable={columnsByTableQ.data?.names ?? {}}
                  pageVarNames={pageVarNames}
                  appQueries={(appQueriesQ.data ?? []).map((aq) => ({
                    id: aq.id,
                    key: aq.key,
                    name: aq.name,
                  }))}
                  preview={previewQ.data?.[activeQuery.name]}
                  onChange={(next) => patchQuery(activeQuery.id, next)}
                  onRemove={() => removeQuery(activeQuery.id)}
                  onRunPreview={runQueryPreview}
                />
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 items-center justify-center bg-background p-6 text-center text-sm text-muted-foreground">
                Nessuna query. Aggiungine una dalla lista a sinistra.
              </div>
            )
          ) : activeKey === "js" ? (
            activeJs ? (
              <div className="min-h-0 flex-1 overflow-hidden bg-background">
                <JsObjectEditorPane
                  jsObject={activeJs}
                  siblingNames={jsObjectsList.filter((o) => o.name !== activeJs.name).map((o) => o.name)}
                  onChange={(next) => patchJsObject(activeJs.name, next)}
                  onRemove={() => removeJsObject(activeJs.name)}
                  previewExpr={loggingPreviewExpr}
                />
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 items-center justify-center bg-background p-6 text-center text-sm text-muted-foreground">
                Nessun JS Object. Aggiungine uno dalla lista a sinistra.
              </div>
            )
          ) : activeKey === "state" ? (
            // WS-8 — the variables LIST is in the sidebar; here is the selected
            // variable's DETAIL, with the page `$input` contract (ADR-0045 R4) as
            // a compact section below — both declare the page's data surface.
            <div className="flex min-h-0 flex-1 flex-col bg-background">
              <div className="min-h-0 flex-1 overflow-hidden p-4">
                <StateEditor
                  view="detail"
                  variables={ast.variables}
                  stateKeys={stateKeys}
                  references={pageRefs}
                  selectedId={selectedVarId}
                  onSelectId={setSelectedVarId}
                  onChange={(variables) => commitAst({ ...ast, variables })}
                  onJump={(u) => {
                    if (ast.queries.some((q) => q.id === u.nodeId)) openQueryTab(u.nodeId);
                    else setSelectedId(u.nodeId);
                  }}
                />
              </div>
              <div className="max-h-64 shrink-0 overflow-y-auto border-t border-border p-4">
                <PageInputsEditor
                  inputs={ast.inputs ?? []}
                  discovered={inputKeys}
                  onChange={(inputs) => commitAst({ ...ast, inputs })}
                />
              </div>
            </div>
          ) : activeKey === "globalState" ? (
            // WS-4b — app-global $global declared bag (DETAIL; list in sidebar).
            <div className="min-h-0 flex-1 overflow-hidden bg-background p-4">
              <GlobalStateEditor
                view="detail"
                entries={globalState}
                onChange={onGlobalStateChange}
                saving={saveGlobalState.isPending}
                selectedIdx={selectedGlobalIdx}
                onSelectIdx={setSelectedGlobalIdx}
              />
            </div>
          ) : activeKey === "jsLibraries" ? (
            // ADR-0046 — per-app external JS libraries (DETAIL; list in sidebar).
            <div className="min-h-0 flex-1 overflow-hidden bg-background p-4">
              <JsLibrariesPane
                view="detail"
                appId={appId}
                selectedName={selectedLibName}
                onSelectName={setSelectedLibName}
              />
            </div>
          ) : activeKey === "customComponents" ? (
            // ADR-0046 — the reusable custom-component library (DETAIL; list in sidebar).
            <div className="min-h-0 flex-1 overflow-hidden bg-background p-4">
              <CustomComponentsPane
                view="detail"
                appId={appId}
                selectedId={selectedComponentId}
                onSelectId={setSelectedComponentId}
              />
            </div>
          ) : activeKey === "appQueries" ? (
            // ADR-0047 — the app-level Query manager (DETAIL; list in sidebar).
            <div className="min-h-0 flex-1 overflow-y-auto bg-background p-4">
              <AppQueriesPane
                view="detail"
                appId={appId}
                tables={(tablesQ.data ?? []).map((t) => ({ id: t.id, name: t.name }))}
                datasets={(datasetsQ.data ?? []).map((d) => ({ id: d.id, name: d.name }))}
                columnsByTable={columnsByTableQ.data?.names ?? {}}
                selectedId={selectedAppQueryId}
                onSelectId={setSelectedAppQueryId}
              />
            </div>
          ) : activeKey === "appTables" ? (
            // ADR-0048 single-IDE — the app data-table manager (DETAIL; list in sidebar).
            <div className="min-h-0 flex-1 overflow-y-auto bg-background p-4">
              <AppTablesPane
                view="detail"
                appId={appId}
                selectedId={selectedAppTableId}
                onSelectId={setSelectedAppTableId}
              />
            </div>
          ) : activeKey === "appDatasets" ? (
            // ADR-0048 single-IDE — the app datasets/projections manager (DETAIL; list in sidebar).
            <div className="min-h-0 flex-1 overflow-y-auto bg-background p-4">
              <AppDatasetsPane
                view="detail"
                appId={appId}
                selectedId={selectedAppDatasetId}
                onSelectId={setSelectedAppDatasetId}
              />
            </div>
          ) : activeKey === "appForms" ? (
            // ADR-0048 single-IDE — the app forms manager (DETAIL; list in sidebar).
            <div className="min-h-0 flex-1 overflow-y-auto bg-background p-4">
              <AppFormsPane
                view="detail"
                appId={appId}
                selectedId={selectedAppFormId}
                onSelectId={setSelectedAppFormId}
              />
            </div>
          ) : activeKey === "appLinkedDatasets" ? (
            // ADR-0059 — external Datasets the app consumes (DETAIL; list in sidebar).
            <div className="min-h-0 flex-1 overflow-y-auto bg-background">
              <LinkedResourcesPane
                view="detail"
                appId={appId}
                kind="dataset"
                selectedId={selectedLinkedDatasetId}
                onSelectId={setSelectedLinkedDatasetId}
              />
            </div>
          ) : activeKey === "appLinkedKbs" ? (
            // ADR-0059 — external Knowledge bases the app consumes (DETAIL; list in sidebar).
            <div className="min-h-0 flex-1 overflow-y-auto bg-background">
              <LinkedResourcesPane
                view="detail"
                appId={appId}
                kind="kb"
                selectedId={selectedLinkedKbId}
                onSelectId={setSelectedLinkedKbId}
              />
            </div>
          ) : activeKey === "appLinkedReports" ? (
            // ADR-0060 S5 — Report Types the app consumes (DETAIL; list in sidebar).
            <div className="min-h-0 flex-1 overflow-y-auto bg-background">
              <LinkedResourcesPane
                view="detail"
                appId={appId}
                kind="report"
                selectedId={selectedLinkedReportId}
                onSelectId={setSelectedLinkedReportId}
              />
            </div>
          ) : activeKey === "appMembers" ? (
            // ADR-0048 single-IDE — Members & roles (its own two-column nav+panel; ADR-0059).
            <div className="min-h-0 flex-1 overflow-hidden bg-background">
              <AppAccessSections appId={appId} canWrite={true} />
            </div>
          ) : activeKey === "appInterfaceChat" || activeKey === "appInterfaceVoice" ? (
            // ADR-0049 Slice B — Chat & Voce are SEPARATE flat interfaces; each tab
            // focuses InterfacesSection on its single kind (independent brains).
            <div className="min-h-0 flex-1 overflow-y-auto bg-background p-4">
              <InterfacesSection
                appId={appId}
                canWrite={true}
                focusKind={activeKey === "appInterfaceVoice" ? "voice" : "chat"}
              />
            </div>
          ) : activeKey === "appRest" ? (
            // ADR-0051 — REST endpoints editor (DETAIL; list in the sidebar).
            <div className="min-h-0 flex-1 overflow-y-auto bg-background p-4">
              <RestInterfaceEditor
                view="detail"
                appId={appId}
                canWrite={true}
                selectedId={selectedRestEndpointId}
                onSelectId={setSelectedRestEndpointId}
              />
            </div>
          ) : activeKey === "appRestDocs" ? (
            // ADR-0051 — the live OpenAPI reference (DETAIL; endpoint nav in the
            // standard sidebar, uniform with the other list areas).
            <div className="min-h-0 flex-1 overflow-y-auto bg-background p-4">
              <RestApiDocs view="detail" appId={appId} selectedAnchor={selectedRestDocAnchor} />
            </div>
          ) : activeKey === "appGraphql" ? (
            // ADR-0058 — GraphQL resources editor (DETAIL; list in the sidebar).
            <div className="min-h-0 flex-1 overflow-y-auto bg-background p-4">
              <GraphqlInterfaceEditor
                view="detail"
                appId={appId}
                canWrite={true}
                selectedId={selectedGraphqlResourceId}
                onSelectId={setSelectedGraphqlResourceId}
              />
            </div>
          ) : activeKey === "appGraphqlDocs" ? (
            // ADR-0058 — the GraphiQL playground (test + live introspection docs).
            // Single-pane, full-bleed (GraphiQL owns its own scroll/chrome), lazy.
            <div className="min-h-0 flex-1 bg-background">
              <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Caricamento GraphiQL…</div>}>
                <GraphqlPlayground appId={appId} />
              </Suspense>
            </div>
          ) : activeKey === "appScheduledRules" ? (
            // ADR-0048 single-IDE — Scheduled rules, self-contained (single-pane).
            <div className="min-h-0 flex-1 overflow-y-auto bg-background p-4">
              <ScheduledRulesSection appId={appId} tables={tablesQ.data ?? []} canWrite={true} />
            </div>
          ) : activeKey === "appAutomations" ? (
            // ADR-0048 single-IDE — app automations (workflows), self-contained (single-pane).
            // The list opens the workflow-plane editor (/workflows/:id) — a separate tool.
            <div className="min-h-0 flex-1 overflow-y-auto bg-background p-4">
              <AppAutomationsSection appId={appId} canWrite={true} />
            </div>
          ) : activeKey === "settings" ? (
            // WS-4b — portal settings rendered INLINE in the main pane (was the
            // header gear modal). Mounted only while the Settings tab is active.
            <div className="min-h-0 flex-1 overflow-y-auto bg-background">
              {appQ.data && (
                <PortalSettingsModal
                  inline
                  appId={appId}
                  app={appQ.data}
                  pages={pageTargets}
                  open
                  onOpenChange={() => {}}
                />
              )}
            </div>
          ) : pageless ? (
            // ADR-0049 Slice C — page-less builder on the WEB interface: there is
            // no page to render. Invite the operator to create the web app's first
            // page (this declares the web interface; Slice C2's launcher does the
            // same from the empty-app entry).
            <div className="flex min-h-0 flex-1 items-center justify-center bg-background p-8">
              <div className="max-w-md text-center">
                <h3 className="text-sm font-semibold text-foreground">Nessuna web app</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Questa app non ha ancora una web app. Crea la prima pagina per iniziare a
                  costruirla.
                </p>
                <Button
                  className="mt-4"
                  disabled={createPage.isPending}
                  onClick={() => createPage.mutate("page")}
                >
                  <Plus className="h-4 w-4" /> Crea pagina
                </Button>
              </div>
            </div>
          ) : (
        <main
          // `tray-light` pins the canvas to the LIGHT theme regardless of
          // Tray's dark/light toggle — the portal is light-only, so the
          // WYSIWYG preview must be light too.
          className="min-h-0 flex-1 tray-light overflow-hidden bg-elevated p-6 text-foreground"
          style={{
            backgroundImage: "radial-gradient(hsl(var(--border)) 1px, transparent 1px)",
            backgroundSize: "16px 16px",
          }}
          onClick={() => setSelectedId("root")}
        >
          {/* The "device" fills the canvas height; its width follows the
              viewport selector. The app shell (or bare page) lives inside and
              fills it — you edit the content region in context. */}
          <div
            className="mx-auto h-full overflow-hidden rounded-lg border border-border bg-background shadow-sm transition-[width] duration-200"
            style={{
              width:
                viewport === "custom"
                  ? customW || "100%"
                  : viewport === "mobile"
                    ? 400
                    : viewport === "tablet"
                      ? 768
                      : "100%",
              ...(viewport === "custom" && customH && customH !== "auto" ? { height: customH } : {}),
            }}
          >
            {settings.overlay ? (
              // Overlay page (ADR-0023): preview inside a modal/drawer box on a
              // dimmed backdrop, so the author sees it as it surfaces at runtime
              // — never wrapped in the portal shell (it's not a nav route).
              <div
                className={`flex h-full bg-slate-900/20 ${
                  settings.overlay_mode === "modal" ? "items-center justify-center p-6" : "justify-end"
                }`}
              >
                <div
                  className={`overflow-y-auto border border-border bg-background shadow-xl ${
                    settings.overlay_mode === "modal"
                      ? "max-h-full w-full max-w-2xl rounded-lg p-6"
                      : "h-full w-full max-w-md border-l p-6"
                  }`}
                >
                  <LibraryCodeContext.Provider value={libraryCodeMap}>
                    {canvasContent}
                  </LibraryCodeContext.Provider>
                </div>
              </div>
            ) : showShell && appQ.data?.slug ? (
              // The REAL portal shell (same component as prod). In embedded mode
              // its CHROME is interactive (so the author can collapse the
              // sidebar / open the mobile drawer), but nav links are inert (no
              // navigation) — the page body slot is where editing happens.
              <div className="h-full select-none">
                <PortalShell
                  embedded
                  appSlug={appQ.data.slug}
                  activePageSlug={settings.slug}
                  user={null}
                  onSignOut={() => {}}
                >
                  <LibraryCodeContext.Provider value={libraryCodeMap}>
                    {canvasContent}
                  </LibraryCodeContext.Provider>
                </PortalShell>
              </div>
            ) : (
              <div className="h-full overflow-y-auto bg-background p-6">
                <LibraryCodeContext.Provider value={libraryCodeMap}>
                  {canvasContent}
                </LibraryCodeContext.Provider>
              </div>
            )}
          </div>
        </main>
          )}
        </div>

        {/* Inspector — UI context ONLY. Every other area (Query/JS/Stato/
            librerie/componenti/impostazioni) owns its whole main pane and has no
            inspector, so we DON'T render this column there (an empty "available
            only in UI" hint was misleading). */}
        {showInspector && (
        <aside className="overflow-y-auto border-l border-border p-3">
          <>
          <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
            {selected ? specByType.get(selected.type)?.label ?? selected.type : "Inspector"}
          </h3>
          {selected && selectedSpec ? (
            <>
              {/* Properties | Style tab toggle (S15). */}
              <div className="mb-3 flex rounded-md border border-border p-0.5 text-xs">
                {(["props", "style"] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    aria-pressed={inspectorTab === tab}
                    onClick={() => setInspectorTab(tab)}
                    className={`flex-1 rounded px-2 py-1 font-medium capitalize transition-colors ${
                      inspectorTab === tab
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {tab === "props" ? "Properties" : "Style"}
                  </button>
                ))}
              </div>
              {inspectorTab === "props" ? (
                <>
                {selected.id !== "root" && selected.type !== "page" && (
                  <NodeIdField
                    id={selected.id}
                    validate={(c) => (ast ? validateNodeId(ast.root, selected.id, c) : null)}
                    onRename={(newId) => {
                      if (!ast) return;
                      commitRoot(renameNodeId(ast.root, selected.id, newId));
                      setSelectedId(newId);
                    }}
                  />
                )}
                {selected.id !== "root" && selected.type !== "page" && (
                  <NodeUsageSummary refs={pageRefs} nodeId={selected.id} />
                )}
                {selected.id !== "root" && selected.type !== "page" && (
                  <div className="mb-3 border-b border-border pb-3">
                    <VisibilityEditor
                      value={String(selected.visible_when ?? "")}
                      onChange={(expr) => mutateNode((r) => setVisibleWhen(r, selected.id, expr))}
                      exprAmbientDts={exprAmbientDts}
                      previewExpr={previewExpr}
                      fields={conditionFields}
                      fieldTypes={conditionFieldTypes}
                    />
                  </div>
                )}
                <PropsInspector
                  node={selected}
                  spec={selectedSpec}
                  queryNames={ast.queries.map((q) => q.name)}
                  queries={ast.queries.map((q) => ({ name: q.name, source_kind: q.source_kind }))}
                  jsFunctions={jsFunctions}
                  forms={(formsQ.data ?? []).map((f) => ({ id: f.id, name: f.name }))}
                  tables={(tablesQ.data ?? []).map((t) => ({ id: t.id, name: t.name }))}
                  varNames={ast.variables.map((v) => v.name)}
                  onCreateVariable={(name) => {
                    // Register a new declared variable (no anonymous state) —
                    // ignore a name that's already declared.
                    if (ast.variables.some((v) => v.name === name)) return;
                    commitAst({
                      ...ast,
                      variables: [
                        ...ast.variables,
                        { id: genId(new Set(ast.variables.map((v) => v.id))), name, initial: null },
                      ],
                    });
                  }}
                  pages={pageTargets}
                  overlayPages={overlayPages}
                  modalTargets={modalTargets}
                  reportOptions={reportOptions}
                  columnOptions={(() => {
                    // For a `record`, offer the bound table's columns.
                    if (selected.type === "record") {
                      return (recordTableQ.data?.columns ?? [])
                        .filter((c) => c.kind === "data")
                        .map((c) => c.name);
                    }
                    // For a data widget, suggest the columns of its bound query
                    // (derived from the preview's first row — no extra fetch).
                    // The query is the `data` binding (ADR-0044 U1) — except the
                    // `page_filter`, whose design-time aid rides its own
                    // `query_ref` prop.
                    const ref = designTimeQueryRef(selected);
                    const rows = previewQ.data?.[ref]?.rows ?? [];
                    return rows.length > 0 ? Object.keys(rows[0]) : [];
                  })()}
                  columnsByTable={columnsByTableQ.data?.names ?? {}}
                  workflows={runnableQ.data ?? []}
                  customComponents={(customComponentsQ.data ?? []).map((c) => ({
                    id: c.id,
                    name: c.name,
                  }))}
                  exprAmbientDts={exprAmbientDts}
                  previewExpr={previewExpr}
                  registerInsert={registerInsert}
                  loadColumnValues={
                    appQ.data?.slug && designTimeQueryRef(selected)
                      ? async (column: string) => {
                          const r = await portalApi.queryPageDistinct(
                            appQ.data!.slug,
                            settings.slug,
                            designTimeQueryRef(selected),
                            column,
                          );
                          return r.values;
                        }
                      : undefined
                  }
                  onChange={(props) => mutateNode((r) => updateProps(r, selected.id, props))}
                />
                </>
              ) : (
                <StylePanel
                  style={selected.style ?? {}}
                  componentType={selected.type}
                  onChange={(style) => mutateNode((r) => updateStyle(r, selected.id, style))}
                />
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Select a component.</p>
          )}
          </>
        </aside>
        )}
          </div>
        </div>
      </div>

      {/* Bottom dock (full-width, resizable) — the dev Console (WS-3): observe,
          don't edit. Response/Logs/Linter/Stato/Riferimenti. Query/JS/Stato
          AUTHORING lives in full-height workspace tabs (WS-1/WS-2). */}
      <BuilderConsole
        queries={ast.queries}
        preview={previewQ.data ?? {}}
        variables={ast.variables}
        stateKeys={stateKeys}
        lintIssues={lintIssues}
        logs={logs}
        onClearLogs={clearLogs}
        response={lastResponse}
        responseNonce={responseNonce}
        pageState={(exprScope.$page ?? {}) as Record<string, unknown>}
        globalState={(exprScope.$global ?? {}) as Record<string, unknown>}
        user={previewResult.user}
        role={previewResult.role}
        itemColumns={repeaterColumns}
        isOverlay={settings.overlay}
        inputKeys={inputKeys}
        onInsert={insertBinding}
        open={dataDockOpen}
        onOpenChange={setDataDockOpen}
        height={dataDockHeight}
        onHeightChange={setDataDockHeight}
      />

      {createOpen && (
        <NewPageDialog
          creating={createPage.isPending}
          onClose={() => setCreateOpen(false)}
          onCreate={(kind) => createPage.mutate(kind)}
        />
      )}

      {/* WS-6 — per-page metadata, opened from the breadcrumb page menu (was the
          inline header strip). Also hosts the page DELETE. */}
      {pageSettingsOpen && settings && (
        <PageSettingsDialog
          settings={settings}
          onChange={(patch) => setSettings((s) => (s ? { ...s, ...patch } : s))}
          onDelete={() => {
            if (window.confirm(`Eliminare la pagina "${settings.title}"?`)) {
              deletePage.mutate(pageId);
              setPageSettingsOpen(false);
            }
          }}
          onClose={() => setPageSettingsOpen(false)}
        />
      )}
    </div>
  );
}

/** Create-page dialog (ADR-0023): choose an ordinary nav Page vs an overlay
 *  Modal/Drawer. Overlay pages are authored like any page but opened by an
 *  `open_overlay` action (kept out of the portal nav). */
function NewPageDialog({
  creating,
  onClose,
  onCreate,
}: {
  creating: boolean;
  onClose: () => void;
  onCreate: (kind: "page" | "modal" | "drawer") => void;
}) {
  const OPTIONS: { kind: "page" | "modal" | "drawer"; title: string; desc: string }[] = [
    { kind: "page", title: "Page", desc: "A navigable page shown in the portal." },
    { kind: "modal", title: "Modal", desc: "Overlay centered over the page (open_overlay)." },
    { kind: "drawer", title: "Drawer", desc: "Overlay sliding in from the right (open_overlay)." },
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="New page"
        className="relative w-full max-w-md rounded-lg border border-border bg-surface p-5 shadow-xl"
      >
        <h3 className="mb-3 text-sm font-semibold">New page</h3>
        <div className="flex flex-col gap-2">
          {OPTIONS.map((o) => (
            <button
              key={o.kind}
              type="button"
              disabled={creating}
              onClick={() => onCreate(o.kind)}
              className="flex flex-col gap-0.5 rounded-md border border-border px-3 py-2 text-left transition-colors hover:border-accent hover:bg-accent/5 disabled:opacity-50"
            >
              <span className="text-sm font-medium">{o.title}</span>
              <span className="text-xs text-muted-foreground">{o.desc}</span>
            </button>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** WS-6 — per-page metadata form (Title/Slug/Access/Home/In sidebar/Overlay) +
 *  the page DELETE. Opened from the breadcrumb page menu; replaces the inline
 *  meta strip that used to bloat the editor header. */
type PageSettingsForm = Pick<
  PageOut,
  "title" | "slug" | "access" | "is_home" | "show_in_nav" | "overlay" | "overlay_mode"
>;

function PageSettingsDialog({
  settings,
  onChange,
  onDelete,
  onClose,
}: {
  settings: PageSettingsForm;
  onChange: (patch: Partial<PageSettingsForm>) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Impostazioni pagina"
        className="relative w-full max-w-md rounded-lg border border-border bg-surface p-5 shadow-xl"
      >
        <h3 className="mb-3 text-sm font-semibold">Impostazioni pagina</h3>
        <div className="flex flex-col gap-3">
          <LabeledInput label="Titolo" value={settings.title} onChange={(v) => onChange({ title: v })} />
          <LabeledInput label="Slug" value={settings.slug} onChange={(v) => onChange({ slug: v })} />
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Accesso</span>
            <select
              className="rounded border border-border bg-background px-2 py-1.5 text-sm"
              value={settings.access}
              onChange={(e) => onChange({ access: e.target.value })}
            >
              {ACCESS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={settings.is_home} onChange={(e) => onChange({ is_home: e.target.checked })} />
            Home page
          </label>
          <label
            className="flex items-center gap-2 text-sm"
            title="Le pagine di dettaglio (aperte da una row-action / drawer) restano nascoste dalla sidebar del portale."
          >
            <input type="checkbox" checked={settings.show_in_nav} onChange={(e) => onChange({ show_in_nav: e.target.checked })} />
            Mostra nella sidebar
          </label>
          {settings.overlay && (
            <div className="flex flex-col gap-1" title="Questa pagina si apre come overlay (azione open_overlay), non come rotta navigabile.">
              <span className="text-xs font-medium uppercase tracking-wide text-accent">Overlay</span>
              <select
                aria-label="Overlay mode"
                className="rounded border border-border bg-background px-2 py-1.5 text-sm"
                value={settings.overlay_mode}
                onChange={(e) => onChange({ overlay_mode: e.target.value as "modal" | "drawer" })}
              >
                <option value="modal">Modal</option>
                <option value="drawer">Drawer</option>
              </select>
            </div>
          )}
        </div>
        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            onClick={onDelete}
            className="flex items-center gap-1 rounded-md px-2 py-1.5 text-sm text-danger hover:bg-danger/10"
          >
            <Trash2 className="h-3.5 w-3.5" /> Elimina pagina
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-elevated"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <input
        className="rounded border border-border bg-background px-2 py-1.5 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function OutlineNode({
  node,
  depth,
  selectedId,
  onSelect,
  onMove,
  onDrop,
  onDelete,
  previewBp,
}: {
  node: PageNode;
  depth: number;
  selectedId: string;
  onSelect: (id: string) => void;
  onMove: (id: string, dir: -1 | 1) => void;
  /** HTML5 drag-drop reorder: drop the dragged node onto this one. */
  onDrop: (dragId: string, dropId: string) => void;
  onDelete: (id: string) => void;
  /** Active viewport-preview breakpoint — rows hidden there are flagged, since
   *  the node has vanished from the canvas (S15b). */
  previewBp: Breakpoint;
}) {
  const [dragOver, setDragOver] = useState(false);
  const isRoot = node.id === "root";
  // Hidden at the current preview breakpoint → dropped from the canvas; flag it
  // here so the only place it's still reachable makes that obvious.
  const hiddenHere = isHiddenAtBreakpoint(node.style, previewBp);
  return (
    <div>
      <div
        // The root is a drop target (drop INTO the page) but not draggable.
        draggable={!isRoot}
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.setData("text/plain", node.id);
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          if (!dragOver) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
          const dragId = e.dataTransfer.getData("text/plain");
          if (dragId) onDrop(dragId, node.id);
        }}
        className={`flex items-center gap-1 rounded px-1 py-0.5 text-sm ${
          dragOver ? "ring-1 ring-accent" : ""
        } ${selectedId === node.id ? "bg-accent/20" : "hover:bg-surface"}`}
        style={{ paddingLeft: depth * 14 + 4 }}
      >
        <button
          className={`flex flex-1 cursor-grab items-center gap-1.5 text-left ${
            hiddenHere ? "text-muted-foreground/60" : ""
          }`}
          onClick={() => onSelect(node.id)}
          title={hiddenHere ? `Hidden on ${previewBp} — shown here so you can still select it` : undefined}
        >
          {node.type}
          {hiddenHere && <EyeOff className="h-3 w-3 shrink-0" />}
        </button>
        {!isRoot && (
          <>
            <button className="px-1 text-muted-foreground hover:text-foreground" onClick={() => onMove(node.id, -1)} title="Up">
              ↑
            </button>
            <button className="px-1 text-muted-foreground hover:text-foreground" onClick={() => onMove(node.id, 1)} title="Down">
              ↓
            </button>
            <button className="px-1 text-danger" onClick={() => onDelete(node.id)} title="Delete">
              ×
            </button>
          </>
        )}
      </div>
      {node.children.map((c) => (
        <OutlineNode
          key={c.id}
          node={c}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
          onMove={onMove}
          onDrop={onDrop}
          onDelete={onDelete}
          previewBp={previewBp}
        />
      ))}
    </div>
  );
}

